'use strict';

/*
 * Cromo Helmet Configurator
 *
 * Copyright © 2026 TCO Works. All rights reserved.
 * Author: TCO Works — https://tcoworks.nl
 * Created: 2026-07-24
 *
 * This source code is the original, proprietary work of TCO Works.
 * Unauthorized copying, reproduction, distribution, or use of this file,
 * in whole or in part, in any form, is prohibited without prior written
 * permission from TCO Works.
 */

// ─── IMAGE URLS ───────────────────────────────────────────────
// To add product photos, set the `image` property on each option object.
// Upload photos to Shopify Admin → Content → Files, copy the CDN URL.
// Example: image: 'https://cdn.shopify.com/s/files/YOUR-IMAGE.jpg'
// Images are optional — color swatches show as fallback when image is null.
const DEFAULT_PREVIEW_IMAGE = 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_custom_design.jpg?v=1783084647';

// ─── COLOR MAP ────────────────────────────────────────────────
const HEX = {
  'Black':'#1a1a1a','Blue':'#1b2a4a','Grey':'#888888','Brown':'#6b4423',
  'Military Green':'#4a5a3a','Dark Green':'#2d4a2d','Turquoise':'#3a8a8a',
  'Golden Sand':'#c9a96e','Peach':'#f4c5a0','White':'#f5f5f5',
  'Royal Blue':'#1a3a6b','Light Grey':'#c0c0c0','Caramel':'#c68642',
  'Bronze':'#8b6914','Bordeaux':'#6b1a2a','Red':'#c0392b','Cerise':'#de3163',
  'Pink':'#e8a0b0','Purple':'#7b2d8b','Medium Blue':'#2a4a8a','Taupe':'#b5a99a',
  'Blue Frost':'#2f2fa2','Dark Blue':'#1b3a6b','Orange':'#e87040',
  'Silver':'#c0c0c0','Gold':'#d4af37','Rosé Gold':'#b76e79','Cream':'#fffdd0',
  'Dark Pink':'#c2185b','Beige':'#b26c43','Green':'#3a6b4a','Powder':'#ffceb7',
  'Light Blue':'#a8c4d4','Chocolat':'#3d1c02','New Gold':'#d4af37',
  'Klimt Silver':'#c0c0c0','Circus gold':'#d4af37','Vesna Black':'#1a1a1a',
  'Vesna Blue':'#1b2a4a','Vesna Bronze':'#8b6914','Croco Black':'#1a1a1a',
  'Croco Blue':'#1b2a4a','Croco Blue Matt':'#2a3a5a','Light Beige':'#faf5ee',
  'Light Green':'#8ab89a','Matt Black':'#2a2a2a','Glossy Black':'#1a1a1a',
  'Black Mist':'#1a1a1a','Single Bias Blue':'#1b2a4a',
  'Izmir Marble Blue':'#3a4a6a','Twist Stripe White':'#f5f5f5',
  'Chrome':'#c0c0c0','Mirror Silver':'#c0c0c0','Rosé':'#b76e79','Metal':'#8c8c8c',
  'Rose Gold':'#b76e79','Crystal':'#e0e0e0','Silver Night':'#7a7a8a',
  'Jet Black':'#1a1a1a','Sapphire':'#1a3a6b','Rose':'#e87090',
  'Amethyst':'#7b2d8b','Champagne':'#f5efe0','Light Siam':'#cc3030',
  'Cocco Black':'#1c1410','Cocco Blue':'#1e3060','Cocco Brown Matt':'#6b4226','Cocco Blue Matt':'#4a6680',
  'Olive Green':'#414225',
};
function h(n) { return HEX[n] || '#cccccc'; }

// ─── PRODUCT DATA ─────────────────────────────────────────────

const COATING_ORDER = ['Shine','Textile','Polish','Metal','Diamond','Texturized','Velvet'];

const VARIANTS = {
  'Shine':      { id: '53410620997975', basePrice: 690 },
  'Textile':    { id: '53410621030743', basePrice: 730 },
  'Polish':     { id: '53410621063511', basePrice: 800 },
  'Metal':      { id: '53410621161815', basePrice: 850 },
  'Diamond':    { id: '53410621194583', basePrice: 930 },
  'Texturized': { id: '53498396442967', basePrice: 700 },
  'Velvet':     { id: '53498393887063',     basePrice: 780 }
};

// Per-color shell price exceptions (override the coating basePrice)
const SHELL_PRICE_EXCEPTIONS = {
  'Textile': { 'Golden Sand': 850 }
};

const SHELL_COLORS = {
  'Shine':      ['Black','Blue'],
  'Textile':    ['Black','Blue','Grey','Brown','Military Green','Dark Green','Turquoise','Golden Sand'],
  'Polish':     ['Black','Blue','Grey','Brown','Military Green','Dark Green','White'],
  'Metal':      ['Royal Blue','Turquoise','Light Grey','Golden Sand','Caramel','Bronze','Bordeaux','Red','Cerise','Pink','Purple','Olive Green'],
  'Diamond':    ['Black','Blue','Medium Blue','Brown','Taupe','Pink'],
  'Texturized': ['Black','Blue Frost','Dark Blue','White','Orange','Red'],
  'Velvet':     ['Black']
};

const SHELL_IMAGES = {
  'Textile': {
    'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Black.jpg?v=1778235995',
    'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Blue.jpg?v=1778235995',
    'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Grey.jpg?v=1778235995',
    'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Brown.jpg?v=1778235995',
    'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Military_Green.jpg?v=1778235995',
    'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Dark_Green.jpg?v=1778235995',
    'Turquoise':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Turquoise.jpg?v=1778235995',
    'Golden Sand':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textile_Golden_Sand.jpg?v=1778235995',
  },
  'Polish': {
    'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Black.jpg?v=1778235995',
    'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Blue.jpg?v=1778235995',
    'Grey':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Grey.jpg?v=1778248947',
    'Brown':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Brown.jpg?v=1778248946',
    'Military Green':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Military_Green.jpg?v=1778248946',
    'Dark Green':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Dark_Green.jpg?v=1778248946',
    'White':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_White.jpg?v=1778248946',
  },
  'Shine': {
    'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Black.jpg?v=1778235995',
    'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Polish_Blue.jpg?v=1778235995',
  },
   'Metal': {
    'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Royal_Blue.jpg?v=1778251060',
    'Turquoise':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Turquoise.jpg?v=1778251060',
    'Light Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Light_Grey.jpg?v=1778251060',
    'Golden Sand':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Golden_Sand.jpg?v=1778251060',
    'Caramel': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Caramel.jpg?v=1778251060',
    'Bronze':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Bronze.jpg?v=1778251060',
    'Bordeaux':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Bordeaux.jpg?v=1778251060',
    'Red': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Red.jpg?v=1778251060',
    'Cerise':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Cerise.jpg?v=1778251060',
    'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Pink.jpg?v=1778251060',
    'Purple':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Metal_Purple.jpg?v=1778251060',
    'Olive Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Metal_Olive_Green_Shell.jpg?v=1784543572',
  },
  'Diamond': {
    'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Black.jpg?v=1778248946',
    'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Blue.jpg?v=1778248946',
    'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Medium_Blue.jpg?v=1778248946',
    'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Brown.jpg?v=1778248946',
    'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Taupe.jpg?v=1778248946',
    'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Diamond_Pink.jpg?v=1778248946',
  },
  'Texturized': {
    'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_Black.jpg?v=1778249783',
    'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_Blue.jpg?v=1778249783',
    'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_Dark_Blue.jpg?v=1778249783',
    'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_White.jpg?v=1778249783',
    'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_Orange.jpg?v=1778249783',
    'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Textured_Red.jpg?v=1778249782',
  },
  'Velvet': {
    'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Configurator_Velvet_Black.jpg?v=1778490211',
  },
};

const DEFAULT_FINISHES = {
  'Shine':      { frame:'Silver',  grid:'Raw',     subgrid:'Raw',    button:'Raw' },
  'Textile':    { frame:'Silver',  grid:'Raw',     subgrid:'Raw',    button:'Raw' },
  'Polish':     { frame:'Polish',  grid:'Polish',  subgrid:'Polish', button:'Polish' },
  'Metal':      { frame:'Metal',   grid:'Metal',   subgrid:'Metal',  button:'Metal' },
  'Diamond':    { frame:'Diamond', grid:'Diamond', subgrid:'Diamond',button:'Diamond' },
  'Texturized': { frame:'Silver',  grid:'Raw',     subgrid:'Raw',    button:'Raw' },
  'Velvet':     { frame:'Silver',  grid:'Raw',     subgrid:'Raw',    button:'Raw' }
};

const BASIC_INSERT = {
  'Shine':      { price:90,  colors:['Black','Blue'],
    colorImages:{
      'Black': 'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPinsertPolishBlackfront.jpg?v=1773401893&width=800',
      'Blue':  'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoinsertPolishBlue2.jpg?v=1773401893&width=800',
    },
    rearColorImages:{
      'Black': 'https://abouthelmets.com/cdn/shop/files/KEPinsertPolishBlackback.jpg?v=1773401893&width=600',
      'Blue':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishBlue3.jpg?v=1773401893&width=600',
    }},
  'Textile':    { price:90,  colors:['Black','Blue','Grey','Brown','Military Green','Dark Green','Turquoise','Golden Sand','Peach'],
    colorImages:{
      'Black':          'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_black.jpg?v=1778223307&width=800',
      'Blue':           'https://abouthelmets.com/cdn/shop/files/KEP_Cromo_insert_Textile_Blue_2.jpg?v=1773413013&width=800',
      'Grey':           'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_grey.jpg?v=1778223263&width=800',
      'Brown':          'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_brown.jpg?v=1778223325&width=800',
      'Military Green': 'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_military_green.jpg?v=1778223233&width=800',
      'Dark Green':     'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_jaguar_green.jpg?v=1778223285&width=800',
      'Turquoise':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileTurquoise2.jpg?v=1773412650&width=800',
      'Golden Sand':    'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileGoldenSand2.jpg?v=1773412650&width=800',
      'Peach':          'https://abouthelmets.com/cdn/shop/files/KEP_Textile_front_insert_peach.jpg?v=1778224215&width=800',
    },
    rearColorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Textile_Back_Black.jpg?v=1784544736',
      'Blue':           'https://abouthelmets.com/cdn/shop/files/KEP_Cromo_insert_Textile_Blue_3.jpg?v=1773413013&width=600',
      'Grey':           'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileGrey3.jpg?v=1773412650&width=600',
      'Brown':          'https://abouthelmets.com/cdn/shop/files/KEP_Textile_back_insert_brown.jpg?v=1778223470&width=600',
      'Military Green': 'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileArmyGreen3.jpg?v=1773412650&width=600',
      'Dark Green':     'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileJaguarGreen3.jpg?v=1773412650&width=600',
      'Turquoise':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileTurquoise3.jpg?v=1773412650&width=600',
      'Golden Sand':    'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertTextileGoldenSand3.jpg?v=1773412650&width=600',
      'Peach':          'https://abouthelmets.com/cdn/shop/files/KEP_Textile_back_insert_peach.jpg?v=1778224215&width=600',
    }},
  'Polish':     { price:90,  colors:['Black','Blue','Grey','Brown','Military Green','Dark Green','White'],
    colorImages:{
      'Black':          'https://abouthelmets.com/cdn/shop/files/KEPinsertPolishBlackfront.jpg?v=1773401893&width=800',
      'Blue':           'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishBlue2.jpg?v=1773401893&width=800',
      'Grey':           'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishGrey2.jpg?v=1773401893&width=800',
      'Brown':          'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishBrown2.jpg?v=1773401893&width=800',
      'Military Green': 'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishArmyGreen2.jpg?v=1773401893&width=800',
      'Dark Green':     'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishDarkGreen2.jpg?v=1773401893&width=800',
      'White':          'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishWhite2.jpg?v=1773401893&width=800',
    },
    rearColorImages:{
      'Black':          'https://abouthelmets.com/cdn/shop/files/KEPinsertPolishBlackback.jpg?v=1773401893&width=600',
      'Blue':           'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishBlue3.jpg?v=1773401893&width=600',
      'Grey':           'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishGrey3.jpg?v=1773401893&width=600',
      'Brown':          'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishBrown3.jpg?v=1773401893&width=600',
      'Military Green': 'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishArmyGreen3.jpg?v=1773401893&width=600',
      'Dark Green':     'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishDarkGreen3.jpg?v=1773401893&width=600',
      'White':          'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertPolishWhite3.jpg?v=1773401893&width=600',
    }},
  'Metal':      { price:90,  colors:['Royal Blue','Turquoise','Light Grey','Golden Sand','Caramel','Bronze','Bordeaux','Red','Cerise','Pink','Purple','Light Blue','Olive Green'],
    colorImages:{
      'Royal Blue':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalRoyalBlue2.jpg?v=1773409813&width=800',
      'Turquoise':   'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalTurquoise2.jpg?v=1773409813&width=800',
      'Light Grey':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalLightGrey2.jpg?v=1773409813&width=800',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_inserts_Metal_Golden_Sand.jpg?v=1784708982',
      'Caramel':     'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalCaramel2.jpg?v=1773409813&width=800',
      'Bronze':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalBronze2.jpg?v=1773409813&width=800',
      'Bordeaux':    'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalBurgundy2.jpg?v=1773409813&width=800',
      'Red':         'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalRed2.jpg?v=1773409813&width=800',
      'Cerise':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalCerise2.jpg?v=1773409813&width=800',
      'Pink':        'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalPink2.jpg?v=1773409813&width=800',
      'Purple':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalPurple2.jpg?v=1773409813&width=800',
      'Light Blue':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalLightBlue2.jpg?v=1773409813&width=800',
      'Olive Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Front_insert_Metal_Olive_Green.jpg?v=1784543856',
    },
    rearColorImages:{
      'Royal Blue':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalRoyalBlue3.jpg?v=1773409813&width=600',
      'Turquoise':   'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalTurquoise3.jpg?v=1773409813&width=600',
      'Light Grey':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalLightGrey3.jpg?v=1773409813&width=600',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_inserts_Metal_Golden_Sand_back.jpg?v=1784709206',
      'Caramel':     'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalCaramel3.jpg?v=1773409813&width=600',
      'Bronze':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalBronze3.jpg?v=1773409813&width=600',
      'Bordeaux':    'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalBurgundy3.jpg?v=1773409813&width=600',
      'Red':         'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalRed3.jpg?v=1773409813&width=600',
      'Cerise':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalCerise3.jpg?v=1773409813&width=600',
      'Pink':        'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalPink3.jpg?v=1773409813&width=600',
      'Purple':      'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalPurple3.jpg?v=1773409813&width=600',
      'Light Blue':  'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertMetalLightBlue3.jpg?v=1773409813&width=600',
      'Olive Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Metal_Olive_Green.jpg?v=1784543952',
    }},
  'Diamond':    { price:110, colors:['Black','Blue','Medium Blue','Brown','Taupe','Pink'], 
    colorImages:{
      'Black':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Black.jpg?v=1778255712',
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Blue.jpg?v=1778255712',
      'Medium Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Medium_Blue.jpg?v=1778255712',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Brown.jpg?v=1778255712',
      'Taupe':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Taupe.jpg?v=1778255712',
      'Pink':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Diamond_Pink.jpg?v=1778255712',
    },
    rearColorImages:{
      'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_black.jpg?v=1784209749',
      'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_blue.jpg?v=1784209750',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_medium_blue.jpg?v=1784209749',
      'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_brown.jpg?v=1784209750',
      'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_taupe.jpg?v=1784209749',
      'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_diamond_pink.jpg?v=1784209750',
    }},
  'Texturized': { price:70,  colors:['Black','Blue Frost','Dark Blue','White','Orange','Red'],
    colorImages:{
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPTexturizedinsertfrontBlack.jpg?v=1778492913',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPTexturizedinsertfrontFrostBlue.jpg?v=1778492913',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPLeatherinsertfrontdarkblue.jpg?v=1778492913',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPLeatherinsertfrontwhite.jpg?v=1778492913',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPTexturizedinsertfrontOrange.jpg?v=1778492913',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPTexturizedinsertfrontRed.jpg?v=1778492913',
    },
    rearColorImages:{
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_Black.jpg?v=1784210739',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_Blue_Frost.jpg?v=1784210739',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_Dark_Blue.jpg?v=1784210739',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_White.jpg?v=1784210739',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_Orange.jpg?v=1784210739',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Texturized_Red.jpg?v=1784210739',
    }},
  'Velvet':     { price:90,  colors:['Black'],
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVelvetBlackinsertfront.jpg?v=1775894696',
    },
    rearColorImages:{
      'Black': 'https://abouthelmets.com/cdn/shop/files/KEPVelvetBlackinsertback.jpg?v=1775894916&width=600',
    }}
};

// Basic insert coating tabs shown in steps 2 & 3 (no Velvet — uses premium insert instead)
const BASIC_INSERT_COATINGS = ['Shine','Textile','Polish','Metal','Diamond','Texturized'];

const LEATHER_COLORS = ['Black','Blue','Grey','Brown','Royal Blue','Bordeaux','White','Orange','Red','Cream','Dark Pink','Taupe','Pink','Beige','Green','Powder'];

const INSERT_TYPES = [
  { name:'Mirror',          price:110, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMirrorBlackfrontinsert.jpg?v=1772197017',
    colorImages:{
      'Black':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMirrorBlackfrontinsert.jpg?v=1772197017',
      'Silver':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMirrorChromefrontinsert.jpg?v=1736416023',
      'Gold':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMirrorGoldfrontinsert.jpg?v=1772197017',
      'Rosé Gold':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMirrorRoseGoldfrontinsert.jpg?v=1772197017',
    },
    rearColorImages:{
      'Black':    'https://abouthelmets.com/cdn/shop/files/KEPMirrorBlackbackinsert.jpg?v=1736345751&width=600',
      'Silver':   'https://abouthelmets.com/cdn/shop/files/KEPMirrorChromebackinsert.jpg?v=1772197017&width=600',
      'Gold':     'https://abouthelmets.com/cdn/shop/files/KEPMirrorGoldbackinsert.jpg?v=1772197017&width=600',
      'Rosé Gold':'https://abouthelmets.com/cdn/shop/files/KEPMirrorRoseGoldbackinsert.jpg?v=1772197017&width=600',
    }, colors:['Black','Silver','Gold','Rosé Gold'],
    colorPrices:{'Gold':150,'Rosé Gold':150} },
  { name:'Suede',           price:140, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPSuedeBlackfrontinsert.jpg?v=1736523363', 
    colorImages:{
      'Black':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPSuedeBlackfrontinsert.jpg?v=1736523363',
      'Blue':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPSuedeBluefrontinsert.jpg?v=1772198021',
      'Brown':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_front_suede_Brown.jpg?v=1786706203',
    },
    rearColorImages:{
      'Black': 'https://abouthelmets.com/cdn/shop/files/KEPSuedeBlackbackinsert.webp?v=1772198021&width=600',
      'Blue':  'https://abouthelmets.com/cdn/shop/files/KEPSuedeBluebackinsert.webp?v=1772198021&width=600',
    }, colors:['Black','Blue','Brown','Grey'] },
  { name:'Velvet',          price:100, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloPolishZwartSparklingVelvetZW131.jpg?v=1736345158',
    colorImages:{
      'Black': 'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPVelvetBlackinsertfront.jpg?v=1775894696&width=800',
    },
    rearColorImages:{
      'Black': 'https://abouthelmets.com/cdn/shop/files/KEPVelvetBlackinsertback.jpg?v=1775894916&width=600',
    }, colors:['Black'] },
  { name:'Leather',         price:140, image:null,
    colorImages:{
      'Black':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherBlackfrontinsert.jpg?v=1772368774&width=800',
      'Blue':      'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherBluefrontinsert.jpg?v=1772368774&width=800',
      'Grey':      'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoGreyLeatherfrontinsert.jpg?v=1772368774&width=800',
      'Brown':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherBrownfrontinsert.jpg?v=1772368774&width=800',
      'Royal Blue':'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherRoyalBluefrontinsert.jpg?v=1772368774&width=800',
      'Bordeaux':  'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherBordeauxfrontinsert.jpg?v=1772368774&width=800',
      'White':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherWhitefrontinsert.jpg?v=1772368774&width=800',
      'Orange':    'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherOrangefrontinsert.jpg?v=1772368774&width=800',
      'Red':       'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoRedLeatherfrontinsert.jpg?v=1772368774&width=800',
      'Cream':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherCreamfrontinsert.jpg?v=1772368774&width=800',
      'Dark Pink': 'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherFuchsiafrontinsert.jpg?v=1772368774&width=800',
      'Taupe':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherTaupefrontinsert.jpg?v=1772368774&width=800',
      'Pink':      'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherPinkfrontinsert.jpg?v=1772368774&width=800',
      'Beige':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherBeigefrontinsert.jpg?v=1772368774&width=800',
      'Green':     'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPCromoLeatherGreenfrontinsert.jpg?v=1772368774&width=800',
      'Powder':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_front_insert_Leather_Powder.jpg?v=1779022564',
    },
    rearColorImages:{
      'Black':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherBlackbackinsert.jpg?v=1772368774&width=600',
      'Blue':      'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherBluebackinsert.jpg?v=1772368774&width=600',
      'Grey':      'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherGreybackinsert.jpg?v=1772368774&width=600',
      'Brown':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherBrownbackinsert.jpg?v=1772368774&width=600',
      'Royal Blue':'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherRoyalBluebackinsert.jpg?v=1772368774&width=600',
      'Bordeaux':  'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherBurgundybackinsert.jpg?v=1772368774&width=600',
      'White':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherWhitebackinsert.jpg?v=1772368774&width=600',
      'Orange':    'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherOrangebackinsert.jpg?v=1772368774&width=600',
      'Red':       'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherRedbackinsert.jpg?v=1772368774&width=600',
      'Cream':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherCreambackinsert.jpg?v=1772368774&width=600',
      'Dark Pink': 'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherFuchsiabackinsert.jpg?v=1772368774&width=600',
      'Taupe':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherTaupebackinsert.jpg?v=1772368774&width=600',
      'Pink':      'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherPinkbackinsert.jpg?v=1772368774&width=600',
      'Beige':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherBeigebackinsert.jpg?v=1772368774&width=600',
      'Green':     'https://abouthelmets.com/cdn/shop/files/KEPCromoLeatherGreenbackinsert.jpg?v=1772368774&width=600',
      'Powder':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_rear_insert_Leather_Powder.jpg?v=1779023196',
    }, colors:LEATHER_COLORS },
  { name:'Glitter Leather', price:220, image:null, frontOnly:true,
    colorImages:{
      'Gold':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPCromoinsertLeatherGlitterGold2.jpg?v=1773425690',
      'Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPCromoinsertLeatherGlitterSilver2.jpg?v=1773425690',
    },
    colors:['Gold','Silver'] },
  { name:'Cocco Leather', price:220, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPolishZwartCoccoSwarovskiZW171.jpg?v=1736345172', colorImages:{
      'Cocco Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Cocco_Leather_Black_insert.jpg?v=1778502434',
      'Cocco Blue':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Cocco_Leather_Blue_insert.jpg?v=1778502435',
      'Cocco Brown Matt':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPCromoinsertLeatherCoccoBrown.jpg?v=1773426156',
      'Cocco Blue Matt': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPCoccoLeatherMattBlueinsert.jpg?v=1778503043',
    },
    colors:['Cocco Black','Cocco Blue','Cocco Brown Matt','Cocco Blue Matt'],
    colorPrices:{'Cocco Brown Matt':250,'Cocco Blue Matt':250},
    rearColorImages:{
      'Cocco Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Cocco_Leather_Black_insert_back.jpg?v=1778502434',
      'Cocco Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Cocco_Leather_Black_insert_blue.jpg?v=1778502435',
    },
    rearColors:['Cocco Black','Cocco Blue'] },
  { name:'Glitter',         price:220, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/PolishBrownGlitterBrownFront.jpg?v=1736345072',
    colorImages:{
      'Black': 'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPGlitterBlackfrontinsert.jpg?v=1772201645&width=800',
      'Blue':  'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPGlitterBluefrontinsert.jpg?v=1772201645&width=800',
      'Brown': 'https://51axsela1jff3dvi-77326713175.shopifypreview.com/cdn/shop/files/KEPGlitterBrownfrontinsert.jpg?v=1736506042&width=800',
    },
    rearColorImages:{
      'Black': 'https://abouthelmets.com/cdn/shop/files/KEPGlitterBlackbackinsert.jpg?v=1772201645&width=600',
      'Blue':  'https://abouthelmets.com/cdn/shop/files/KEPGlitterBluebackinsert.jpg?v=1772201645&width=600',
      'Brown': 'https://abouthelmets.com/cdn/shop/files/KEPGlitterBrownbackinsert.jpg?v=1772201645&width=600',
    }, colors:['Black','Blue','Brown'] },
  { name:'Galassia Glitter',price:220, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPolishZwartGalassiaZW81.jpg?v=1736345145',
    colorImages:{
      'Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPGalassiaBlackfrontinsert.jpg?v=1772199233',
      'Blue':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPGalassiaBluefrontinsert.jpg?v=1736503685',
      'Light Blue':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPGalassiaLightBluefrontinsert.jpg?v=1772199233',
      'Dark Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPGalassiaDarkBluefrontinsert.jpg?v=1772199233',
      'Pink':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPGalassiaPinkfrontinsert.jpg?v=1772199233',
      'New Gold':  'https://abouthelmets.com/cdn/shop/files/KEPinsertNewGalassiaGold.jpg?v=1772225885&width=600',
      'Chocolat':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_front_Galassia_Chocolate_Brown.jpg?v=1786706297',
    },
    rearColorImages:{
      'Black':     'https://abouthelmets.com/cdn/shop/files/KEPGalassiaBlackbackinsert.jpg?v=1772199233&width=600',
      'Blue':      'https://abouthelmets.com/cdn/shop/files/KEPGalassiaBluebackinsert.jpg?v=1772199233&width=600',
      'Light Blue':'https://abouthelmets.com/cdn/shop/files/KEPGalassiaLightBluebackinsert.jpg?v=1772199233&width=600',
      'Dark Blue': 'https://abouthelmets.com/cdn/shop/files/KEPGalassiaDarkBluebackinsert.jpg?v=1772199233&width=600',
      'Pink':      'https://abouthelmets.com/cdn/shop/files/KEPGalassiaPinkbackinsert.jpg?v=1772199233&width=600',
      'New Gold':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_rear_New_Galassia_Gold.jpg?v=1784898861',
    }, colors:['Black','Blue','Light Blue','Dark Blue','Pink','Chocolat','New Gold'],
    colorPrices:{'New Gold':230} },
  { name:'Star Glitter',    price:220, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPolishZwartStarZW191.jpg?v=1736345178',
    colorImages:{
      'Black':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPStarBlackfrontinsert.jpg?v=1772200625',
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPStarBluefrontinsert.jpg?v=1772200625',
      'Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPStarSilverfrontinsert.jpg?v=1772200625',
      'Gold':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPStarGoldfrontinsert_274944c1-d5d7-4531-aa1c-a84de7b21cc8.jpg?v=1772200625',
      'Pink':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPStarPinkfrontinsert.jpg?v=1736505223',
    },
    rearColorImages:{
      'Black':  'https://abouthelmets.com/cdn/shop/files/KEPStarBlackbackinsert.jpg?v=1772200625&width=600',
      'Blue':   'https://abouthelmets.com/cdn/shop/files/KEPStarBluebackinsert.jpg?v=1772200625&width=600',
      'Silver': 'https://abouthelmets.com/cdn/shop/files/KEPStarSilverbackinsert.jpg?v=1772200625&width=600',
      'Gold':   'https://abouthelmets.com/cdn/shop/files/KEPStarGoldbackinsert.jpg?v=1772200625&width=600',
      'Pink':   'https://abouthelmets.com/cdn/shop/files/KEPStarPinkbackinsert.jpg?v=1772200625&width=600',
    }, colors:['Black','Blue','Silver','Gold','Pink'] },
  { name:'Luminor',         price:230, image:null,
    colorImages:{
      'Black':  'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBlackfrontinsert_de041530-71a9-4010-933a-bfa53cd6584f.jpg?v=1772202869&width=600',
      'Blue':   'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBluefrontinsert.webp?v=1772202869&width=600',
      'Silver': 'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorSilverfrontinsert_23a1649e-deee-45b9-a6b1-f0946e658205.jpg?v=1772202869&width=600',
      'Bronze': 'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBronzeinsertsparepart.jpg?v=1762535669&width=600',
    },
    rearColorImages:{
      'Black':  'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBlackbackinsert_994ce7a2-6e14-42cb-bb1a-0b555693ba3a.jpg?v=1772202869&width=600',
      'Blue':   'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBluebackinsert_c6978555-37be-4705-978c-c858c7835c2b.jpg?v=1772202869&width=600',
      'Silver': 'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorSilverbackinsert_4ad2b119-947a-408b-8fc1-bbc61910c674.jpg?v=1772202869&width=600',
      'Bronze': 'https://abouthelmets.com/cdn/shop/files/KEPItaliaLuminorBronzebackinsert_f18603f4-4e53-476a-beaa-64760d4713b5.jpg?v=1772202869&width=600',
    }, colors:['Black','Blue','Silver','Bronze'] },
  { name:'Vesna', price:220, image:null,
    colorImages:{
      'Vesna Black':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Vesna_Black_insert_front.jpg?v=1778562706',
      'Vesna Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVesnaBlackBlueinsertfront.jpg?v=1778562746',
      'Vesna Bronze': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVesnaBlackBronzeinsertfront.jpg?v=1778562745',
    },
    rearColorImages:{
      'Vesna Black':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Vesna_Black_insert_rear.jpg?v=1778562706',
      'Vesna Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVesnaBlackBlueinsertrear.jpg?v=1778562746',
      'Vesna Bronze': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVesnaBlackBronzeinsertrear.jpg?v=1778562745',
    }, colors:['Vesna Black','Vesna Blue','Vesna Bronze'] },
  { name:'Goccia',          price:300, image:null,
    colorImages:{
      'Black':      'https://abouthelmets.com/cdn/shop/files/KEPinsertGocciaBlack.jpg?v=1772225387&width=800',
      'Blue':       'https://abouthelmets.com/cdn/shop/files/KEP_insert_Goccia_Blue.jpg?v=1772225426&width=800',
      'Powder':     'https://abouthelmets.com/cdn/shop/files/KEP_Front_insert_Goccia_Powder.jpg?v=1778244601&width=800',
      'Light Beige':'https://abouthelmets.com/cdn/shop/files/KEP_Front_insert_Goccia_Light_Beige.jpg?v=1778244613&width=800',
    }, colors:['Black','Blue','Powder','Light Beige'] },
  { name:'Verona',          price:340, image:null,
    frontOnly:true,
    splitColors:[
      ['Black','Black'],       ['Black','Mirror Silver'],
      ['Blue','Blue'],         ['Blue','Mirror Silver'],
      ['Beige','Black'],       ['Beige','Mirror Silver'],
      ['Brown','Brown'],       ['Brown','Mirror Silver'],
      ['Taupe','Taupe'],       ['Taupe','Mirror Silver'],
    ],
    colorImages:{
      'Black – Black':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Black_Black.jpg?v=1779030659',
      'Black – Mirror Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Black_Mirror.jpg?v=1779030660',
      'Blue – Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Blue_Blue.jpg?v=1779031441',
      'Blue – Mirror Silver':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Blue_Chrome.jpg?v=1779031441',
      'Beige – Black':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Beige_Black.jpg?v=1779030660',
      'Beige – Mirror Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Beige_Mirror.jpg?v=1779030660',
      'Brown – Brown':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Brown_Brown.jpg?v=1779034124',
      'Brown – Mirror Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Brown_Chrome.jpg?v=1779034124',
      'Taupe – Taupe':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Taupe_Taupe.jpg?v=1779034124',
      'Taupe – Mirror Silver': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Verona_insert_Taupe_Chrome.jpg?v=1779034124',
    },
    rearColorImages:{} },
  { name:'Vanity',          price:720, image:null, colorImages:{
      'Black':     'https://abouthelmets.com/cdn/shop/files/KEPVanitySwarovskiinsertBlack.jpg?v=1777900070&width=600',
      'Blue':      'https://abouthelmets.com/cdn/shop/files/KEPVanitySwarovskiinsertBlue.jpg?v=1777900069&width=600',
      'Silver':    'https://abouthelmets.com/cdn/shop/files/KEPVanitySwarovskisparepartsilver.jpg?v=1777900070&width=600',
      'Champagne': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_font_Vanity_Gold_Champagne.jpg?v=1784894314',
    }, frontOnly:true, colors:['Black','Blue','Silver','Champagne'] },
  { name:'Nebula Shaded Swarovski',price:610, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPolishZwartShadedSwarovskiGoudZW31.jpg?v=1736345128', colorImages:{
      'Black – Gold':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_Gold.jpg?v=1781171640',
      'Black – Silver':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_Silver.jpg?v=1781171638',
      'Black – Rose Gold':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_Rose_Gold.jpg?v=1781171638',
      'Blue – Gold':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Blue_Gold.jpg?v=1781171638',
      'Blue – Silver':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Blue_Silver.jpg?v=1781171638',
      'Blue – Rose Gold':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Blue_Rose_Gold.jpg?v=1781171639',
      'Brown – Gold':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Brown_Gold.jpg?v=1781171639',
      'Brown – Silver':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Brown_Silver.jpg?v=1781171640',
      'Beige – Gold':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Beige_Gold.jpg?v=1781171638',
      'Light Beige – Gold':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Light_Beige_Gold.jpg?v=1781171638',
      'Grey – Silver':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Grey_Silver.jpg?v=1781171639',
      'Light Green – Silver':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Light_Green_Silver.jpg?v=1781171639',
      'Green – Silver':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Green_Silver.jpg?v=1781171638',
    }, frontOnly:true,
    splitColors:[
      ['Black','Gold'],['Black','Silver'],['Black','Rose Gold'],
      ['Blue','Gold'],['Blue','Silver'],['Blue','Rose Gold'],
      ['Brown','Gold'],['Brown','Silver'],['Beige','Gold'],
      ['Light Beige','Gold'],['Grey','Silver'],['Light Green','Silver'],['Green','Silver']
    ],
    colorPrices:{
      'Brown – Gold':620,'Brown – Silver':620,'Beige – Gold':620,
      'Light Beige – Gold':620,'Grey – Silver':620,
      'Light Green – Silver':620,'Green – Silver':620
    }},
  { name:'Nebula Shaded Swarovski Flag', price:460, image:null, colorImages:{
      'Italy – Black':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_Italian_Flag_Swarovski.jpg?v=1784714588',
      'Italy – Blue':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Blue_Italian_Flag_Swarovski.jpg?v=1784714589',
      'Italy Stripe – Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_Italian_Flag_Stripe_Swarovski.jpg?v=1784714589',
      'China – Black':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Nebula_Black_China_Flag_Swarovski.jpg?v=1784714589',
    }, frontOnly:true,
    splitColors:[
      ['Italy','Black'],['Italy','Blue'],
      ['Italy Stripe','Black'],
      ['China','Black']
    ],
    colorPrices:{
      'Italy Stripe – Black':450,
      'China – Black':700
    }},
  { name:'Cubic Print', price:120, image:null, colorImages:{
      'Black Mist':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Cubic_Print_Black_Mist.jpg?v=1781254061',
      'Single Bias Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Cubic_Print_Single_Bias_Blue.jpg?v=1781254061',
      'Izmir Marble Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Cubic_Print_Izmir_Marble_Blue.jpg?v=1781254061',
      'Twist Stripe White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_insert_Cubic_Print_Twist_Stripe_White.jpg?v=1781254062',
    },
    rearColorImages:{
      'Black Mist':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Cubic_Print_Black_Mist.jpg?v=1784211683',
      'Single Bias Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Cubic_Print_Single_Bias_Blue.jpg?v=1784211683',
      'Izmir Marble Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Cubic_Print_Izmir_Marble_Blue.jpg?v=1784211683',
      'Twist Stripe White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Cubic_Print_Twist_Stripe_White.jpg?v=1784211683',
    }, priceRear:610, pills:['Black Mist','Single Bias Blue','Izmir Marble Blue','Twist Stripe White'] },
  { name:'Pegasus',     price:120, image:null,
    colorImages:{
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusBlufrontinsert.jpg?v=1736511937',
      'Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusGreyfrontinsert.jpg?v=1772204118',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusAranciofrontinsert.jpg?v=1772204118',
      'Pink':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusRosafrontinsert.jpg?v=1772204118',
    },
    rearColorImages:{
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusBlubackinsert.jpg?v=1736511993',
      'Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusGreybackinsert.jpg?v=1772204118',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusAranciobackinsert.jpg?v=1772204118',
      'Pink':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPegasusRosabackinsert.jpg?v=1772204118',
    }, colors:['Blue','Grey','Orange','Pink'] },
  { name:'Vikings',     price:120, image:null, colorImages:{
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsBlufrontinsert.jpg?v=1772204729',
      'Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsGreyfrontinsert.jpg?v=1772204729',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsAranciofrontinsert.jpg?v=1736517587',
      'Pink':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsRosafrontinsert.jpg?v=1772204729',
    },
    rearColorImages:{
      'Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsBlubackinsert_c434ed36-2106-41a6-b109-51f56d20ef72.jpg?v=1772204729',
      'Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsGreybackinsert.jpg?v=1772204729',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsAranciobackinsert.jpg?v=1772204729',
      'Pink':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPVikingsRosabackinsert.jpg?v=1772204729',
    }, colors:['Blue','Grey','Orange','Pink'] },
  { name:'Milano Leather',        price:140, image:null, colorImages:{
      'Black':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoLeatherBlackfrontinsert.jpg?v=1736506726',
      'Blue':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoLeatherBluefrontinsert.jpg?v=1736507304',
      'Bordeaux':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Milano_Leather_Burgundy_front_insert.jpg?v=1736510468',
      'Beige':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoLeatherBeigefrontinsert.jpg?v=1736510977',
      'Brown':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Milano_Leather_Brown_insert_front.jpg?v=1778564226',
    }, frontOnly:true, colors:['Black','Blue','Bordeaux','Beige','Brown'] },
  { name:'Milano Satin',          price:100, image:null, colorImages:{
      'Black':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoBlackfrontinsert.jpg?v=1736506649',
      'Blue':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoBluefrontinsert.jpg?v=1736507231',
      'Bordeaux':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoBurgundyfrontinsert.jpg?v=1736510356',
      'Beige':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoBeigefrontinsert.jpg?v=1736510906',
    }, frontOnly:true, colors:['Black','Blue','Bordeaux','Beige'] },
  { name:'Milano Cross',          price:340, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloPolishBlackMilanoCrossZW661.jpg?v=1736345527', colorImages:{
      'Black':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoCrossBlackfrontinsert.jpg?v=1736506781',
      'Blue':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoCrossBluefrontinsert.jpg?v=1736507347',
      'Bordeaux':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoCrossBurgundyfrontinsert.jpg?v=1736510521',
      'Beige':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoCrossBeigefrontinsert.jpg?v=1736511020',
    }, frontOnly:true, colors:['Black','Blue','Bordeaux','Beige'] },
  { name:'Milano Street',         price:440, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloPolishBlackMilanoStreetCristalliZW651.jpg?v=1736345524', colorImages:{
      'Black':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoStreetBlackfrontinsert.jpg?v=1736506850',
      'Blue':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoStreetBluefrontinsert.jpg?v=1736507410',
      'Bordeaux':'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoStreetBurgundyfrontinsert.jpg?v=1736510452',
      'Beige':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoStreetBeigefrontinsert.jpg?v=1736511066',
    }, frontOnly:true, colors:['Black','Blue','Bordeaux','Beige'] },
  { name:'Milano Shining Velvet', price:290, image:null,
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoShiningVelvetBlackinsert.jpg?v=1778524359',
      'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Milano_Shining_Velvet_Blue_insert.jpg?v=1778524514',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoShiningVelvetBrowninsert.jpg?v=1778524359',
      'Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPMilanoShiningVelvetGreeninsert.jpg?v=1778524360',
    }, frontOnly:true, colors:['Black','Blue','Brown','Green'] },
  { name:'Other Fabrics',   price:220, image:null,
    colorImages:{
      'Klimt Silver': 'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertGlitterKlimt2.jpg?v=1773426655&width=600',
      'Circus gold':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPCircusGoldGlitterinsert.jpg?v=1778505309',
    },
    rearColorImages:{
      'Klimt Silver': 'https://abouthelmets.com/cdn/shop/files/KEPCromoinsertGlitterKlimt3.jpg?v=1773426655&width=600',
      'Circus gold':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Back_insert_Glitter_Circus_Gold.jpg?v=1784211683',
    }, colors:['Klimt Silver','Circus gold'] },
];

// Visor pricing: standard price + additional polo price
const VISOR_STD = {
  'Textile/Polish/Textured/Shine': { default:0,  diamond:0 },
  'Metal':                         { default:0,  diamond:0 },
  'Diamond':                       { default:10, diamond:0 },
  'Velvet':                        { default:30, diamond:30, velvet:0 },
  'Leather':                       { default:65, diamond:55 }
};
const VISOR_POLO = {
  'Textile/Polish/Textured/Shine': { default:30,  diamond:15 },
  'Metal':                         { default:30,  diamond:15 },
  'Diamond':                       { default:40,  diamond:30 },
  'Velvet':                        { default:50,  diamond:40, velvet:30 },
  'Leather':                       { default:120, diamond:110 }
};

// Visor color options per material tab
const VISOR_COLORS = {
  'Shine':      ['Black','Blue'],
  'Textile':    BASIC_INSERT['Textile'].colors.filter(c => c !== 'Peach'),
  'Polish':     BASIC_INSERT['Polish'].colors,
  'Metal':      BASIC_INSERT['Metal'].colors.filter(c => c !== 'Light Blue'),
  'Diamond':    BASIC_INSERT['Diamond'].colors,
  'Texturized': BASIC_INSERT['Texturized'].colors,
  'Velvet':     ['Black'],
  'Leather':    LEATHER_COLORS
};

// Maps visor material tab → VISOR_STD/POLO key
const VISOR_COAT_KEY = {
  'Shine':      'Textile/Polish/Textured/Shine',
  'Textile':    'Textile/Polish/Textured/Shine',
  'Polish':     'Textile/Polish/Textured/Shine',
  'Texturized': 'Textile/Polish/Textured/Shine',
  'Velvet':     'Velvet',
  'Metal':      'Metal',
  'Diamond':    'Diamond',
  'Leather':    'Leather'
};

const VISOR_IMAGES = {
  Standard: {
    'Shine': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Black.webp?v=1772206031',
      'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Blue.webp?v=1772206031',
    },
    'Textile': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Black.jpg?v=1772205374',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Blue.jpg?v=1772205374',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Grey.jpg?v=1772205374',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Brown.jpg?v=1772205374',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Military_Green.jpg?v=1772205374',
      'Dark Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Dark_Green.jpg?v=1772205374',
      'Turquoise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Turquoise.jpg?v=1762535418',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Textile_visor_Golden_Sand.jpg?v=1772205374',
    },
    'Polish': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Black.webp?v=1772206031',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Blue.webp?v=1772206031',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Grey.webp?v=1772206031',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Brown.webp?v=1772205937',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Military_Green.webp?v=1772206031',
      'Dark Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_Dark_Green.webp?v=1772206031',
      'White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polish_Visor_White.webp?v=1772206031',
    },
    'Metal': {
      'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Royal_Blue.jpg?v=1772206686',
      'Turquoise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Turquoise.jpg?v=1772206686',
      'Light Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Grey.jpg?v=1772206686',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Golden_Sand.jpg?v=1772206686',
      'Caramel': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Caramel.jpg?v=1772206686',
      'Bronze': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Bronze.jpg?v=1772206686',
      'Bordeaux': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Burgundy.jpg?v=1772206686',
      'Red': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Red.jpg?v=1772206686',
      'Cerise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Cerise.jpg?v=1772206686',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Pink.jpg?v=1772206686',
      'Purple': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Metal_visor_Purple.jpg?v=1772206686',
      'Olive Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Metal_Olive_Green.jpg?v=1784900169',
    },
    'Diamond': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaStandardDiamondBlack.webp?v=1772207212',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Diamond_Blue.jpg?v=1772207212',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Diamond_Medium_Blue.jpg?v=1772207212',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Diamond_Brown.jpg?v=1772207212',
      'Taupe': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Diamond_Taupe.jpg?v=1772207212',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Standard_Diamond_Pink.jpg?v=1772207212',
    },
    'Texturized': {
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_Black.jpg?v=1784897529',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_Blue_Frost.jpg?v=1784897529',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_Blue.jpg?v=1784897528',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_White.jpg?v=1784897528',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_Orange.jpg?v=1784897528',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Texturized_Redjpg.jpg?v=1784897529',
    },
    'Velvet': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Velvet_Black.jpg?v=1784898227',
    },
    'Leather': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Black.jpg?v=1772224282',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Blue.jpg?v=1772224803',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Grey.jpg?v=1772224494',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Brown.jpg?v=1772224338',
      'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Royal_Blue.jpg?v=1772224603',
      'Bordeaux': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Bordeaux_Burgundy.jpg?v=1772224417',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Orange.jpg?v=1772224541',
      'Red': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Red.jpg?v=1772224572',
      'Cream': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Cream.jpg?v=1772224462',
      'Dark Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Fuchsia.jpg?v=1772224524',
      'Taupe': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Taupe.jpg?v=1772224371',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Pink.jpg?v=1772224675',
      'Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Standard_visor_Leather_Green.jpg?v=1772369227',
      'Beige': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Leather_Beige.jpg?v=1784898521',
      'White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Leather_White.jpg?v=1784898520',
      'Powder': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Visor_Standard_Leather_Cipria_Powder.jpg?v=1784898521',
    },
  },
  Polo: {
    'Shine': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloPolishVisorBlack.jpg?v=1762535415',
      'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Blue.jpg?v=1772206031',
    },
    'Textile': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloTextilevisorBlack_aadd0653-1965-45f4-aacd-8cb07b9111e5.jpg?v=1762535445',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Textile_visor_Blue.jpg?v=1772205374',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Textile_visor_Grey.jpg?v=1772205374',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Textile_visor_Brown.jpg?v=1772205374',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Textile_visor_Military_Green.jpg?v=1772205374',
      'Dark Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Textile_visor_Dark_Green.jpg?v=1772205374',
      'Turquoise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_Textile_visor_Turquoise.jpg?v=1772205374',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_Textile_visor_Golden_Sand.jpg?v=1772205374',
    },
    'Polish': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPItaliaPoloPolishVisorBlack.jpg?v=1762535415',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Blue.jpg?v=1772206031',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Grey.jpg?v=1772206031',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Brown.jpg?v=1772206031',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Military_Green.jpg?v=1772206031',
      'Dark Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_Dark_Green.jpg?v=1772206031',
      'White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Polish_Visor_White.jpg?v=1772206031',
    },
    'Metal': {
      'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Royal_Blue.jpg?v=1772206686',
      'Turquoise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Turquoise.jpg?v=1772206686',
      'Light Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Light_Grey.jpg?v=1772206686',
      'Golden Sand': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Golden_Sand.jpg?v=1772206686',
      'Caramel': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Caramel.jpg?v=1772206686',
      'Bronze': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Bronze.jpg?v=1772206686',
      'Bordeaux': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Burgundy.jpg?v=1762535423',
      'Red': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Red.jpg?v=1772206686',
      'Cerise': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Cerise.jpg?v=1772206686',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Pink.jpg?v=1772206686',
      'Purple': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Metal_visor_Purple.jpg?v=1772206686',
      'Olive Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Metal_Olive_Green.jpg?v=1784900169',
    },
    'Diamond': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Black.jpg?v=1772207212',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Blue.jpg?v=1772207212',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Medium_Blue.jpg?v=1772207212',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Brown.jpg?v=1772207212',
      'Taupe': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Taupe.jpg?v=1772207212',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Italia_Polo_Diamond_Pink.jpg?v=1772207212',
    },
    'Texturized': {
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_Black.jpg?v=1784897998',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_Blue_Frost.jpg?v=1784897998',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_Dark_Blue.jpg?v=1784897999',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_White.jpg?v=1784897998',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_Orange.jpg?v=1784897998',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Standard_Texturized_Red.jpg?v=1784897998',
    },
    'Velvet': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_Visor_Velvet_Black.jpg?v=1784898225',
    },
    'Leather': {
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Black.jpg?v=1772208996',
      'Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Blue.jpg?v=1772208996',
      'Grey': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Grey.jpg?v=1772208996',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Brown.jpg?v=1772208996',
      'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Royal_Blue.jpg?v=1772208996',
      'Bordeaux': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Bordeaux_Burgundy.jpg?v=1772208996',
      'White': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Wit_60bf9013-2e52-4e1f-9936-fb4ffa0db379.jpg?v=1772224397',
      'Orange': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEPPolovisorLeatherOranje.jpg?v=1772208628',
      'Red': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Red.jpg?v=1772208996',
      'Cream': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Cream.jpg?v=1772208996',
      'Dark Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Fuchsia.jpg?v=1772208996',
      'Taupe': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Taupe.jpg?v=1772208996',
      'Pink': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Pink.jpg?v=1772208996',
      'Beige': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Beige_f8fae355-fd20-4036-8ec8-651a4558e5b0.jpg?v=1772224628',
      'Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_visor_Leather_Green_60766e37-980a-49a3-9aae-0a1a27414ae9.jpg?v=1772369228',
      'Powder': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Polo_VisorLeather_Cipria_Powder.jpg?v=1784898520',
    },
  },
};

// Strap options
const ECO_LEATHER_COLORS = ['Black','Blue','Brown','Grey','Beige'];  // Shine only
const STRAP_LEATHER_STD = ['Black','Blue','Brown','Grey','Beige'];
const STRAP_LEATHER_EXT = ['Royal Blue','Green','White','Cream','Powder','Taupe','Orange','Bordeaux','Red','Dark Pink','Pink'];
const SWAROVSKI_STRAP    = { price:410, combos:[['Black','Black'],['Black','Crystal'],['Blue','Blue'],['Blue','Crystal'],['Beige','Champagne'],['Brown','Champagne']] };
const SNAKE_STRAP_PRICE  = 320;
const SNAKE_COLORS = ['Matt Black','Glossy Black','Silver','Gold'];

const STRAP_IMAGES = {
  'Leather': {
    'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Black.jpg?v=1779016488',
    'Blue':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Navy_Blue.jpg?v=1779016488',
    'Brown':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Brown.jpg?v=1779016488',
    'Grey':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Grey.jpg?v=1779016488',
    'Beige':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Beige.jpg?v=1779014189',
    'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Orange.jpg?v=1779016488',
    'Cream':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Cream.jpg?v=1779016488',
    'Dark Pink':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Dark_Pink_Cerise.jpg?v=1779016488',
    'Bordeaux':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Bordeaux_Burgundy.jpg?v=1779016488',
    'Green':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Green.jpg?v=1779016488',
    'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_White.jpg?v=1779019201',
    'Powder':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Powder.jpg?v=1779019201',
    'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Red.jpg?v=1779019201',
    'Royal Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Royal_Blue.jpg?v=1779019202',
    'Pink':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Pink.jpg?v=1779019202',
    'Taupe':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Taupe.jpg?v=1779019202',
  },
  'Swarovski': {
    'Beige – Champagne': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Beige_Champagne_Swarovski.jpg?v=1778923461',
    'Brown – Champagne': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Brown_Swarovski_Champagne.jpg?v=1779016488',
    'Black – Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Black_Swarovski_Black.jpg?v=1779016489',
    'Black – Crystal':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Black_Swarovski_Crystal.jpg?v=1779016488',
    'Blue – Blue':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Blue_Swarovski_Blue.jpg?v=1779016488',
    'Blue – Crystal':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_strap_Blue_Swarovski_Crystal.jpg?v=1779016488',
  },
  'Snake': {
    'Silver':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Strap_Snake_Silver.jpg?v=1779624311',
    'Gold':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Strap_Snake_Gold.jpg?v=1779624311',
    'Matt Black':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Strap_Snake_Matt_Black.jpg?v=1779624311',
    'Glossy Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Strap_Snake_Glossy_Black.jpg?v=1779624310',
  },
};

// Component step materials
const _sc = SHELL_COLORS;
const _MIRROR_COLORS = [['Black','#1a1a1a'],['Silver','#c0c0c0'],['Gold','#d4af37']];

// Swarovski stone colors for frame — [name, hex fallback, image]
const FRAME_SWAROVSKI_COLORS = [
  ['Crystal',         '#e0e0e0', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Crystal.jpg?v=1784528911'],
  ['Silver Night',    '#8c8c8c', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Silver_Night.jpg?v=1784528911'],
  ['Jet Black',       '#1a1a1a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovsk_Jet_Black.jpg?v=1784528911'],
  ['Sapphire',        '#1a3a8a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Sapphire.jpg?v=1784528911'],
  ['Rose',            '#e8607a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Rose.jpg?v=1784528911'],
  ['Amethyst Purple', '#9b59b6', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Amethyst_Purple.jpg?v=1784528911'],
  ['Champagne',       '#f5e6c8', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Champagne.jpg?v=1784528911'],
  ['Light Siam Red',  '#c0392b', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Light_Siam_Red.jpg?v=1784528911'],
  ['Tangerine',       '#e8742a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Tangerine.jpg?v=1784528911'],
  ['Black Diamond',   '#4a4a4a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Black_Diamond.jpg?v=1784528911'],
  ['Vintage Rose',    '#c48a94', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Vintage_Rose.jpg?v=1784528911'],
  ['Aqua Marine',     '#7fd4d0', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Aqua_Marine.jpg?v=1784528911'],
  ['Light Rose',      '#f3b6c4', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Light_Rose.jpg?v=1784528911'],
  ['Peridot',         '#a8c23a', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Peridot.jpg?v=1784528911'],
  ['Light Topaz',     '#f0c987', 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Light_Topaz.jpg?v=1784528911'],
];
const _SWAROVSKI_COLORS = FRAME_SWAROVSKI_COLORS;

function findSwarovskiColor(name) {
  return FRAME_SWAROVSKI_COLORS.find(c => c[0] === name) || null;
}

const FRAME_MATS = [
  { name:'Matches Shell', image:null, matchesShell:true,                                            prices:{default:0} },
  { name:'Raw',           image:null, colors:[['Black','#1a1a1a'],['Blue','#1b2a4a']],              prices:{default:0},
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Raw_Black.jpg?v=1784718799',
      'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Raw_Blue.jpg?v=1784718799',
    }},
  { name:'Textile',       image:null, colors:BASIC_INSERT['Textile'].colors.map(c=>[c,h(c)]),       prices:{default:0},
    colorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Black.jpg?v=1784472900',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Blue.jpg?v=1784473340',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Military_Green.jpg?v=1784472901',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Brown.jpg?v=1784472900',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Dark_Green.jpg?v=1784472900',
      'Golden Sand':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Golden_Sand.jpg?v=1784472900',
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Grey.jpg?v=1784472901',
      'Turquoise':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Turquoise.jpg?v=1784472901',
      'Peach':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Textile_Peach.jpg?v=1784472900',
    }},
  { name:'Polish',        image:null, colors:_sc.Polish.map(c=>[c,h(c)]),                           prices:{default:0},
    colorImages:{
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Grey.jpg?v=1784539809',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Dark_Green.jpg?v=1784539809',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Blue.jpg?v=1784539809',
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Black.jpg?v=1784539809',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Brown.jpg?v=1784539809',
      'White':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_White.jpg?v=1784539808',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Polish_Military_Green.jpg?v=1784539809',
    }},
  { name:'Metal',         image:null, colors:['Royal Blue','Turquoise','Light Grey','Golden Sand','Caramel','Bronze','Bordeaux','Red','Cerise','Pink','Purple','Olive Green'].map(c=>[c,h(c)]), prices:{default:0},
    colorImages:{
      'Pink':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Pink.jpg?v=1784539810',
      'Bordeaux':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Bordeaux.jpg?v=1784539809',
      'Bronze':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Bronze.jpg?v=1784539809',
      'Olive Green':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Olive_Green.jpg?v=1784539809',
      'Turquoise':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Turquoise.jpg?v=1784539809',
      'Cerise':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Cerise.jpg?v=1784539809',
      'Caramel':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Caramel.jpg?v=1784539809',
      'Red':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Red.jpg?v=1784539808',
      'Royal Blue':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Royal_Blue.jpg?v=1784539809',
      'Golden Sand':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Golden_Sand.jpg?v=1784539809',
      'Light Grey':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Light_Grey.jpg?v=1784539809',
      'Purple':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Metal_Purple.jpg?v=1784539809',
    }},
  { name:'Mirror',        image:null, colors:[['Black','#1a1a1a'],['Silver','#c0c0c0'],['Gold','#d4af37'],['Rosé Gold','#b76e79',{default:15,diamond:0}]], prices:{default:0},
    colorImages:{
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Mirror_Black.jpg?v=1784539809',
      'Gold':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Mirror_Gold.jpg?v=1784539809',
      'Silver':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Mirror_Silver.jpg?v=1784539810',
      'Rosé Gold':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Frame_Mirror_Rose_Gold.jpg?v=1784539809',
    }},
  { name:'Diamond',       image:null, colors:_sc.Diamond.map(c=>[c,h(c)]),                          prices:{default:15,diamond:0},
    colorImages:{
      'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Black.jpg?v=1784718799',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Medium_Blue.jpg?v=1784718800',
      'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Brown.jpg?v=1784718799',
      'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Blue.jpg?v=1784718799',
      'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Taupe.jpg?v=1784718800',
      'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Diamond_Pink.jpg?v=1784718800',
    }},
  { name:'Texturized',    image:null, colors:_sc.Texturized.map(c=>[c,h(c)]),                       prices:{default:0},
    colorImages:{
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_Dark_Blue.jpg?v=1784718800',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_Blue_Frost.jpg?v=1784718799',
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_Black.jpg?v=1784718799',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_Orange.jpg?v=1784718800',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_Red.jpg?v=1784718800',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Texturized_White.jpg?v=1784718800',
    }},
  { name:'Velvet',        image:null, colors:[['Black','#1a1a1a']],                                 prices:{default:20,diamond:5,velvet:0},
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_frame_Velvet_Black.jpg?v=1784718799',
    }}
];
const GRID_MATS = [
  { name:'Matches Shell', image:null, matchesShell:true,                                            prices:{default:0} },
  { name:'Raw',           image:null, colors:[['Black','#1a1a1a'],['Blue','#1b2a4a'],['Grey','#888888'],['Brown','#6b4423']], prices:{default:0},
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Raw_Black.jpg?v=1784882417',
      'Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Raw_Blue.jpg?v=1784882417',
      'Grey':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Raw_Grey.jpg?v=1784882417',
      'Brown': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Raw_Brown.jpg?v=1784882417',
    }},
  { name:'Textile',       image:null, colors:BASIC_INSERT['Textile'].colors.map(c=>[c,h(c)]),       prices:{default:0},
    colorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Black.jpg?v=1784879769',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Blue.jpg?v=1784879769',
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Grey.jpg?v=1784879769',
      'Golden Sand':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Golden_Sand.jpg?v=1784879769',
      'Peach':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Peach.jpg?v=1784879768',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Brown.jpg?v=1784879768',
      'Turquoise':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Turquoise.jpg?v=1784879769',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Military_Green.jpg?v=1784879768',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Textile_Dark_Green.jpg?v=1784879768',
    }},
  { name:'Polish',        image:null, colors:_sc.Polish.map(c=>[c,h(c)]),                           prices:{default:0},
    colorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Black.jpg?v=1784879768',
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Grey.jpg?v=1784879768',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Brown.jpg?v=1784879768',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Blue.jpg?v=1784879768',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Dark_Green.jpg?v=1784879768',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_Military_Green.jpg?v=1784879768',
      'White':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Polish_White.jpg?v=1784880222',
    }},
  { name:'Metal',         image:null, colors:BASIC_INSERT['Metal'].colors.map(c=>[c,h(c)]),         prices:{default:0},
    colorImages:{
      'Turquoise':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Turquoise.jpg?v=1784881603',
      'Olive Green':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Olive_Green.jpg?v=1784881603',
      'Red':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Red.jpg?v=1784881603',
      'Purple':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Purple.jpg?v=1784881603',
      'Pink':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Pink.jpg?v=1784881603',
      'Caramel':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Caramel.jpg?v=1784881604',
      'Bronze':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Bronze.jpg?v=1784881603',
      'Light Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Light_Grey.jpg?v=1784881603',
      'Royal Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Royal_Blue.jpg?v=1784881603',
      'Golden Sand':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Golden_Sand.jpg?v=1784881603',
      'Cerise':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Cerise.jpg?v=1784881603',
      'Bordeaux':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Metal_Bordeaux.jpg?v=1784881604',
    }},
  { name:'Diamond',       image:null, colors:_sc.Diamond.map(c=>[c,h(c)]),                          prices:{default:15,diamond:0},
    colorImages:{
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Medium_Blue.jpg?v=1784881604',
      'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Pink.jpg?v=1784881603',
      'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Blue.jpg?v=1784881603',
      'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Brown.jpg?v=1784881604',
      'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Taupe.jpg?v=1784881603',
      'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Diamond_Black.jpg?v=1784881603',
    }},
  { name:'Texturized',    image:null, colors:_sc.Texturized.map(c=>[c,h(c)]),                       prices:{default:0},
    colorImages:{
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_Dark_Blue.jpg?v=1784730018',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_Blue_Frost.jpg?v=1784730019',
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_Black.jpg?v=1784730018',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_Orange.jpg?v=1784730019',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_Red.jpg?v=1784730018',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Texturized_White.jpg?v=1784730019',
    }},
  { name:'Mirror',        image:null, colors:[['Black','#1a1a1a'],['Silver','#c0c0c0'],['Gold','#d4af37'],['Rosé Gold','#b76e79',{default:45,diamond:30}]], prices:{default:15,diamond:0},
    colorImages:{
      'Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Mirror_Black.jpg?v=1784882417',
      'Silver':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Mirror_Silver.jpg?v=1784882417',
      'Rosé Gold': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Mirror_Rose_Gold.jpg?v=1784882417',
      'Gold':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Mirror_Gold.jpg?v=1784882417',
    }},
  { name:'Velvet',        image:null, colors:[['Black','#1a1a1a']],                                 prices:{default:30,diamond:15,velvet:0},
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Grid_Velvet_Black.jpg?v=1784882417',
    }}
];
// Subgrid Raw shares Textile's photos (same object) — label stays "Raw", image shown is Textile's
// for that color, since a plain Raw subgrid photo isn't clear on its own.
const SUBGRID_TEXTILE_IMAGES = {
  'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Black.jpg?v=1784887927',
  'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Grey.jpg?v=1784887927',
  'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Blue.jpg?v=1784887927',
  'Turquoise':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Turquoise.jpg?v=1784887927',
  'Peach':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Peach.jpg?v=1784887927',
  'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Brown.jpg?v=1784887927',
  'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Dark_Green.jpg?v=1784887927',
  'Golden Sand':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Golden_Sand.jpg?v=1784887927',
  'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Textile_Military_Green.jpg?v=1784887927',
};
const SUBGRID_MATS = [
  { name:'Matches Shell', image:null, matchesShell:true,                                            prices:{default:0} },
  { name:'Raw',           image:null, colors:[['Black','#1a1a1a'],['Blue','#1b2a4a'],['Grey','#888888'],['Brown','#6b4423']], prices:{default:0},
    colorImages: SUBGRID_TEXTILE_IMAGES },
  { name:'Textile',       image:null, colors:BASIC_INSERT['Textile'].colors.map(c=>[c,h(c)]),       prices:{default:0},
    colorImages: SUBGRID_TEXTILE_IMAGES },
  { name:'Polish',        image:null, colors:_sc.Polish.map(c=>[c,h(c)]),                           prices:{default:0},
    colorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Black.jpg?v=1784887927',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Blue.jpg?v=1784887927',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Dark_Green.jpg?v=1784887927',
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Grey.jpg?v=1784887927',
      'White':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_White.jpg?v=1784887927',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Brown.jpg?v=1784887927',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Polish_Military_Green.jpg?v=1784887927',
    }},
  { name:'Metal',         image:null, colors:BASIC_INSERT['Metal'].colors.map(c=>[c,h(c)]),         prices:{default:0},
    colorImages:{
      'Red':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Red.jpg?v=1784885172',
      'Light Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Light_Grey.jpg?v=1784887927',
      'Caramel':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Caramel.jpg?v=1784887927',
      'Turquoise':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Turquoise.jpg?v=1784887927',
      'Purple':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Purple.jpg?v=1784887927',
      'Royal Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Royal_Blue.jpg?v=1784887927',
      'Bordeaux':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Bordeaux.jpg?v=1784887927',
      'Pink':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Pink.jpg?v=1784887927',
      'Cerise':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Cerise.jpg?v=1784887927',
      'Bronze':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Bronze.jpg?v=1784887927',
      'Golden Sand':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Golden_Sand.jpg?v=1784887927',
      'Olive Green':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Metal_Olive_Green.jpg?v=1784887927',
    }},
  { name:'Diamond',       image:null, colors:_sc.Diamond.map(c=>[c,h(c)]),                          prices:{default:10,diamond:0},
    colorImages:{
      'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Black.jpg?v=1784887927',
      'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Taupe.jpg?v=1784887927',
      'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Brown.jpg?v=1784887927',
      'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Blue.jpg?v=1784887927',
      'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Pink.jpg?v=1784887927',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Diamond_Medium_Blue.jpg?v=1784887927',
    }},
  { name:'Texturized',    image:null, colors:_sc.Texturized.map(c=>[c,h(c)]),                       prices:{default:0},
    colorImages:{
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_Black.jpg?v=1784887927',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_Orange.jpg?v=1784887927',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_White.jpg?v=1784887927',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_Red.jpg?v=1784887927',
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_Blue_Frost.jpg?v=1784887927',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Texturized_Blue.jpg?v=1784887927',
    }},
  { name:'Mirror',        image:null, colors:[['Black','#1a1a1a'],['Silver','#c0c0c0'],['Gold','#d4af37'],['Rosé Gold','#b76e79',{default:30,diamond:15}]], prices:{default:15,diamond:0},
    colorImages:{
      'Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Mirror_Black.jpg?v=1784887927',
      'Silver':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Mirror_Silver.jpg?v=1784887927',
      'Rosé Gold': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Mirror_Rose_Gold.jpg?v=1784887927',
      'Gold':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Subgrid_Mirror_Gold.jpg?v=1784887927',
    }}
];
// Button Raw shares Textile's photos (same object) — label stays "Raw", image shown is Textile's
// for that color, same treatment as Subgrid.
const BUTTON_TEXTILE_IMAGES = {
  'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Black.jpg?v=1784892753',
  'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Military_Green.jpg?v=1784892753',
  'Turquoise':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Turquoise.jpg?v=1784892753',
  'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Dark_Green.jpg?v=1784892753',
  'Peach':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Peach.jpg?v=1784892753',
  'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Grey.jpg?v=1784892753',
  'Golden Sand':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Golden_Sand.jpg?v=1784892753',
  'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Blue.jpg?v=1784892753',
  'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Textile_Brown.jpg?v=1784892753',
};
const BUTTON_MATS = [
  { name:'Matches Shell', image:null, matchesShell:true,                                            prices:{default:0} },
  { name:'Raw',           image:null, colors:[['Black','#1a1a1a'],['Blue','#1b2a4a'],['Grey','#888888'],['Brown','#6b4423']], prices:{default:0},
    colorImages: BUTTON_TEXTILE_IMAGES },
  { name:'Textile',       image:null, colors:BASIC_INSERT['Textile'].colors.map(c=>[c,h(c)]),       prices:{default:0},
    colorImages: BUTTON_TEXTILE_IMAGES },
  { name:'Polish',        image:null, colors:_sc.Polish.map(c=>[c,h(c)]),                           prices:{default:0},
    colorImages:{
      'Black':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Black.jpg?v=1784892753',
      'Dark Green':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Dark_Green.jpg?v=1784892753',
      'Grey':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Grey.jpg?v=1784892753',
      'Blue':           'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Blue.jpg?v=1784892753',
      'Military Green': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Military_Green.jpg?v=1784892753',
      'Brown':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_Brown.jpg?v=1784892753',
      'White':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Polish_White.jpg?v=1784892753',
    }},
  { name:'Metal',         image:null, colors:BASIC_INSERT['Metal'].colors.map(c=>[c,h(c)]),         prices:{default:0},
    colorImages:{
      'Pink':         'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Pink.jpg?v=1784892753',
      'Light Grey':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Light_Grey.jpg?v=1784892753',
      'Olive Green':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Olive_Green.jpg?v=1784892753',
      'Red':          'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Red.jpg?v=1784892753',
      'Turquoise':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_MetaTurquoise.jpg?v=1784892753',
      'Bronze':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Bronze.jpg?v=1784892753',
      'Golden Sand':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Golden_Sand.jpg?v=1784892753',
      'Bordeaux':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Bordeaux.jpg?v=1784892753',
      'Cerise':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Cerise.jpg?v=1784892753',
      'Purple':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Purple.jpg?v=1784892753',
      'Royal Blue':   'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Royal_Blue.jpg?v=1784892753',
      'Caramel':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Metal_Caramel.jpg?v=1784892753',
    }},
  { name:'Diamond',       image:null, colors:_sc.Diamond.map(c=>[c,h(c)]),                          prices:{default:10,diamond:0},
    colorImages:{
      'Blue':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Blue.jpg?v=1784892753',
      'Taupe':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Taupe.jpg?v=1784892753',
      'Pink':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Pink.jpg?v=1784892753',
      'Brown':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Brown.jpg?v=1784892753',
      'Black':       'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Black.jpg?v=1784892753',
      'Medium Blue': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Diamond_Medium_Blue.jpg?v=1784892753',
    }},
  { name:'Texturized',    image:null, colors:_sc.Texturized.map(c=>[c,h(c)]),                       prices:{default:0},
    colorImages:{
      'Blue Frost': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_Blue_Frost.jpg?v=1784892753',
      'Black':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_Black.jpg?v=1784892753',
      'Red':        'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_Red.jpg?v=1784892753',
      'White':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_White.jpg?v=1784892753',
      'Dark Blue':  'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_Dark_Blue.jpg?v=1784892753',
      'Orange':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Texturized_Orange.jpg?v=1784892753',
    }},
  { name:'Mirror',        image:null, colors:[['Black','#1a1a1a'],['Silver','#c0c0c0'],['Gold','#d4af37'],['Rosé Gold','#b76e79',{default:20,diamond:10}]], prices:{default:0},
    colorImages:{
      'Gold':      'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Mirror_Gold.jpg?v=1784892753',
      'Rosé Gold': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Mirror_Rose_Gold.jpg?v=1784892753',
      'Black':     'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Mirror_Black.jpg?v=1784892753',
      'Silver':    'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Mirror_Silver.jpg?v=1784892753',
    }},
  { name:'Velvet',        image:null, colors:[['Black','#1a1a1a']],                                 prices:{default:20,velvet:0},
    colorImages:{
      'Black': 'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Button_Velvet_Black.jpg?v=1784892754',
    }}
];

const COMPONENT_CFGS = [
  { num:7,  label:'Grid',    stateKey:'grid',    colorKey:'gridColor',    priceKey:'gridPrice',    prev:6,  next:8,  mats:GRID_MATS },
  { num:8,  label:'Subgrid', stateKey:'subgrid', colorKey:'subgridColor', priceKey:'subgridPrice', prev:7,  next:9,  mats:SUBGRID_MATS },
  { num:9,  label:'Button',  stateKey:'button',  colorKey:'buttonColor',  priceKey:'buttonPrice',  prev:8,  next:10, mats:BUTTON_MATS }
];

const LOGO_METALLIC = [
  { name:'Silver Matt',     top:'#c0c0c0', bottom:'#1a1a1a', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Silver_Matt_Black.jpg?v=1780771806' },
  { name:'Silver / Blue',   top:'#c0c0c0', bottom:'#1b2a4a', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Silver_Blue.jpg?v=1780771485' },
  { name:'Silver / Green',  top:'#c0c0c0', bottom:'#3a6b4a', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Green.jpg?v=1780771807' },
  { name:'Silver / Pink',   top:'#c0c0c0', bottom:'#e8a0b0', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Pink.jpg?v=1780771485' },
  { name:'Silver / Red',    top:'#c0c0c0', bottom:'#c0392b', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Red.jpg?v=1780771807' },
  { name:'Silver / Orange', top:'#c0c0c0', bottom:'#e87040', price:0,   image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Metallic_Orange.jpg?v=1780771807' }
];
const LOGO_ZIRCOON = [
  { name:'Silver / Black',    top:'#c0c0c0', bottom:'#1a1a1a', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Black.jpg?v=1780812790' },
  { name:'Silver / Blue',     top:'#c0c0c0', bottom:'#1b2a4a', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Blue.jpg?v=1780812789' },
  { name:'Silver / Green',    top:'#c0c0c0', bottom:'#3a6b4a', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Green.jpg?v=1780812789' },
  { name:'Silver / Red',      top:'#c0c0c0', bottom:'#c0392b', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Red.jpg?v=1780812789' },
  { name:'Silver / Pink',     top:'#c0c0c0', bottom:'#e8a0b0', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Pink.jpg?v=1780812790' },
  { name:'Silver / Yellow',   top:'#c0c0c0', bottom:'#f0d000', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Yellow.jpg?v=1780812790' },
  { name:'Silver / Clear',    top:'#c0c0c0', bottom:'#f5f5f5', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Silver_Clear.jpg?v=1780812789' },
  { name:'Gold / Clear',      top:'#d4af37', bottom:'#f5f5f5', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Gold_Clear.jpg?v=1780812789' },
  { name:'Rose Gold / Clear', top:'#b76e79', bottom:'#f5f5f5', price:200, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Zircoon_Rose_Gold_Clear.jpg?v=1780812789' }
];
const LOGO_FLAGS = [
  { name:'Germany',      code:'de', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Germany.jpg?v=1780744392' },
  { name:'UK',           code:'gb', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Flag_logo_UK.jpg?v=1784901352' },
  { name:'France',       code:'fr', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_France.jpg?v=1780744392' },
  { name:'Spain',        code:'es', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Spain.jpg?v=1780771485' },
  { name:'Portugal',     code:'pt', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Portugal.jpg?v=1780771485' },
  { name:'Italy',        code:'it', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Italy.jpg?v=1780744614' },
  { name:'Poland',       code:'pl', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Poland.jpg?v=1780771485' },
  { name:'Ireland',      code:'ie', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Ireland.jpg?v=1780744393' },
  { name:'Norway',       code:'no', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Norway.jpg?v=1780771485' },
  { name:'Sweden',       code:'se', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Sweden.jpg?v=1780771485' },
  { name:'Denmark',      code:'dk', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Denmark.jpg?v=1780744394' },
  { name:'Finland',      code:'fi', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Finland.jpg?v=1780744394' },
  { name:'Switzerland',  code:'ch', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Switzerland.jpg?v=1781082479' },
  { name:'Austria',      code:'at', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Austria.jpg?v=1780744393' },
  { name:'Israel',       code:'il', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Israel.jpg?v=1780744614' },
  { name:'Ukraine',      code:'ua', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Ukraine.jpg?v=1781082479' },
  { name:'USA',          code:'us', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Flag_logo_USA.jpg?v=1784901353' },
  { name:'Canada',       code:'ca', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Canada.jpg?v=1780744394' },
  { name:'Australia',    code:'au', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Australia.jpg?v=1780744393' },
  { name:'New Zealand',  code:'nz', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_New_Zealand.jpg?v=1780771485' },
  { name:'China',        code:'cn', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_China.jpg?v=1780744393' },
  { name:'Japan',        code:'jp', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Japan.jpg?v=1780771485' },
  { name:'Korea',        code:'kr', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Korea.jpg?v=1780771485' },
  { name:'Kuwait',       code:'kw', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Kuwait.jpg?v=1780771485' },
  { name:'Saudi Arabia', code:'sa', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Flag_Saudi_Arabia.jpg?v=1780771485' },
  { name:'UAE',          code:'ae', price:320, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Swarovski_Flag_logo_UAE.jpg?v=1784901352' }
];
const LOGO_CRYSTAL = { name:'Velvet with Crystal', top:'#1a1a1a', bottom:'#c0c0c0', price:80, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Logo_Velvet_Black_Cristalli.jpg?v=1780812789' };

const NAMETAG_OPTIONS = [
  { name:'None',   price:0,     image:null },
  { name:'Silver', price:42.50, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Nametag_Silver.jpg?v=1778565435' },
  { name:'Gold',   price:47.50, image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Nametag_Gold.jpg?v=1778565435' },
  { name:'Rosé',   price:60,    image:'https://cdn.shopify.com/s/files/1/0773/2671/3175/files/KEP_Nametag_Rose_Gold.jpg?v=1778565434' }
];

// ─── STATE ────────────────────────────────────────────────────
const state = {
  currentStep:1,
  coating:null, shellColor:null, shellColorHex:null,
  frontInsert:null, frontInsertColor:null, frontInsertPrice:0,
  rearInsert:null,  rearInsertColor:null,  rearInsertPrice:0,
  visorType:'Standard', visorMaterial:null, visorColor:'', visorPrice:0,
  strap:null, strapColor:null, strapPrice:0,
  frame:null, frameColor:null, framePrice:0,
  frameSwarovski:false, frameSwarovskiColor:'',
  grid:null,  gridColor:null,  gridPrice:0,
  subgrid:null, subgridColor:null, subgridPrice:0,
  button:null,  buttonColor:null,  buttonPrice:0,
  logo:null, logoPrice:0,
  size:null, lining:null,
  nameTag:'None', nameTagText:'', nameTagPrice:0,
  basePrice:0,
  stepPreviews:{} // { stepNum: { image: null|url, hex: null|'#rrggbb' } }
};

const onCoatingChange = [];
const userSelected = {};  // per-step explicit selection flag, keyed by stepNum
let _strapTab = null;  // active strap type tab (UI state only)
let _stepScrollEnabled = false;  // skip scroll on initial page load

// ─── HELPERS ──────────────────────────────────────────────────
function fmt(v) {
  const [i, d] = v.toFixed(2).split('.');
  return '€ ' + i.replace(/\B(?=(\d{3})+(?!\d))/g, '.') + ',' + d;
}

function getTotal() {
  return state.basePrice + state.frontInsertPrice + state.rearInsertPrice +
    state.visorPrice + state.strapPrice + state.framePrice +
    state.gridPrice + state.subgridPrice + state.buttonPrice +
    state.logoPrice + state.nameTagPrice;
}

function getCompPrice(spec, coating) {
  if (!spec) return 0;
  if (coating === 'Diamond') return spec.diamond !== undefined ? spec.diamond : 0;
  if (coating === 'Shine' && spec.shine !== undefined) return spec.shine;
  if (coating === 'Velvet' && spec.velvet !== undefined) return spec.velvet;
  return spec.default || 0;
}

// Velvet shell: Velvet inserts are included at no charge
function velvetInsertPrice(typeName, price) {
  return (typeName === 'Velvet' && state.coating === 'Velvet') ? 0 : price;
}

// Returns per-color price if defined on the color entry, otherwise falls back to mat.prices
function getColorPrice(mat, colorName, coating) {
  if (colorName && mat.colors) {
    const entry = mat.colors.find(([cn]) => cn === colorName);
    if (entry && entry[2]) return getCompPrice(entry[2], coating);
  }
  return getCompPrice(mat.prices, coating);
}

// Splits state.framePrice into the underlying material price and the Swarovski surcharge
function getFramePriceSplit() {
  if (!state.frame) return { base: 0, swarovski: 0 };
  if (!state.frameSwarovski) return { base: state.framePrice, swarovski: 0 };
  const mat = FRAME_MATS.find(m => m.name === state.frame);
  const base = mat ? getColorPrice(mat, state.frameColor, state.coating) : 0;
  return { base, swarovski: state.framePrice - base };
}

// For a "Matches Shell" component material (Frame/Grid/Subgrid/Button) — looks up the photo of
// the material within matsList that shares its name with the shell coating (e.g. shell
// Textile/Brown → Textile/Brown photo in that list). Returns null if no material with that name
// exists yet in matsList, or it has no photo for that color.
function getMatchesShellImage(matsList) {
  const coatingMat = matsList.find(m => m.name === state.coating);
  if (coatingMat && coatingMat.colorImages && state.shellColor) {
    return coatingMat.colorImages[state.shellColor] || null;
  }
  return null;
}

// Picks the default color for a Grid/Subgrid/Button material: the shell color if that
// material offers it, otherwise the material's first color.
function pickDefaultMatColor(matsList, matName, shellColor) {
  const mat = matsList.find(m => m.name === matName);
  if (!mat || !mat.colors || !mat.colors.length) return null;
  return mat.colors.some(([cn]) => cn === shellColor) ? shellColor : mat.colors[0][0];
}

function setStepChoice(n, t) {
  const el = document.getElementById(`step-${n}-choice`);
  if (el) el.textContent = t ? ` — ${t}` : '';
}

function showToast(msg) {
  const t = document.getElementById('cromo-toast');
  if (!t) return;
  t.textContent = msg;
  t.classList.add('visible');
  clearTimeout(t._tmr);
  t._tmr = setTimeout(() => t.classList.remove('visible'), 5000);
}

// Swatch factory — name shown only as tooltip, no label below
function makeSwatch(colorName, hexVal, { split, splitDir, freeBadge, image } = {}) {
  const btn = document.createElement('button');
  btn.className = 'swatch' + (split ? ' swatch-split' : '') + (image ? ' swatch-image' : '');
  let mediaHtml = '';
  if (image) {
    // Image lives in its own clipped inner element, isolated from the button's box-shadow/border-radius
    // compositing — sharing one element caused faint dark artifacts at the circle's edge when zoomed in.
    // White base color so the sliver between this inner circle and the button's transparent border
    // shows white (matching the product photos) instead of the theme's default black button background.
    btn.style.backgroundColor = '#ffffff';
    mediaHtml = `<span class="swatch-media" style="position:absolute;inset:0;border-radius:50%;overflow:hidden;background-image:url(${image});background-size:145%;background-position:center;background-repeat:no-repeat;"></span>`;
  } else {
    btn.style.background = split
      ? `linear-gradient(${splitDir || '135deg'}, ${hexVal} 50%, ${split} 50%)`
      : hexVal;
  }
  btn.title = colorName;
  btn.innerHTML = `${mediaHtml}<span class="checkmark">&#10003;</span>${freeBadge ? '<span class="swatch-free-badge">Free</span>' : ''}`;
  return btn;
}

function makeFlagSwatch(flagName, countryCode) {
  const btn = document.createElement('button');
  btn.className = 'swatch swatch-flag';
  btn.title = flagName;
  btn.style.backgroundImage = `url(https://flagcdn.com/w40/${countryCode}.png)`;
  btn.innerHTML = `<span class="checkmark">&#10003;</span>`;
  return btn;
}

// Updates the left-column preview area: shows image if available, else hex swatch circle, else empty grey
function setPreview(image, hex) {
  const imgEl = document.getElementById('preview-img');
  const swEl  = document.getElementById('preview-swatch');
  if (!imgEl || !swEl) return;
  if (image) {
    imgEl.src = image;
    imgEl.alt = (state.coating && state.shellColor)
      ? `KEP Cromo 2.0 helmet — ${state.coating} ${state.shellColor}`
      : 'Preview of your custom KEP Cromo 2.0 helmet configuration';
    imgEl.style.display = 'block';
    swEl.style.display  = 'none';
  } else if (hex) {
    imgEl.style.display    = 'none';
    swEl.style.display     = 'block';
    swEl.style.background  = hex;
  } else {
    imgEl.style.display = 'none';
    swEl.style.display  = 'none';
  }
}

// ─── PRICE DISPLAY ────────────────────────────────────────────
function updatePriceDisplay() {
  const s = state.currentStep;
  const rawTotal = (state.basePrice || 0)
    + (s >= 2  ? state.frontInsertPrice : 0)
    + (s >= 3  ? state.rearInsertPrice  : 0)
    + (s >= 4  ? state.visorPrice       : 0)
    + (s >= 5  ? state.strapPrice       : 0)
    + (s >= 6  ? state.framePrice       : 0)
    + (s >= 7  ? state.gridPrice        : 0)
    + (s >= 8  ? state.subgridPrice     : 0)
    + (s >= 9  ? state.buttonPrice      : 0)
    + (s >= 10 ? state.logoPrice        : 0)
    + (s >= 12 ? state.nameTagPrice     : 0);
  const total = rawTotal;
  const bd = document.getElementById('price-breakdown');
  if (bd) {
    const items = [];
    const s = state.currentStep;
    if (state.coating) items.push({ label:`Shell — ${state.coating}${state.shellColor ? ', '+state.shellColor : ''}`, price:state.basePrice, isBase:true });
    const push = (label, price, cond) => { if (cond) items.push({ label, price }); };
    push(`Front Insert — ${state.frontInsert}${state.frontInsertColor ? ' / '+state.frontInsertColor : ''}`, state.frontInsertPrice, !!state.frontInsert && s >= 2);
    push(`Rear Insert — ${state.rearInsert}${state.rearInsertColor ? ' / '+state.rearInsertColor : ''}`, state.rearInsertPrice, !!state.rearInsert && s >= 3);
    push(`Visor — ${state.visorType} / ${state.visorMaterial}${state.visorColor ? ' / '+state.visorColor : ''}`, state.visorPrice, !!state.visorMaterial && s >= 4);
    push(`Strap — ${state.strap}${state.strapColor ? ' / '+state.strapColor : ''}`, state.strapPrice, !!state.strap && s >= 5);
    const _frameSplit = getFramePriceSplit();
    const _frameDesc = state.frame ? `${state.frame}${state.frameColor ? ' / '+state.frameColor : ''}` : '';
    push(`Frame — ${_frameDesc}`, _frameSplit.base, !!state.frame && s >= 6);
    push(`Frame Swarovski — ${state.frameSwarovskiColor || ''}`, _frameSplit.swarovski, state.frameSwarovski && s >= 6);
    push(`Grid — ${state.grid}${state.gridColor ? ' / '+state.gridColor : ''}`, state.gridPrice, !!state.grid && s >= 7);
    push(`Subgrid — ${state.subgrid}${state.subgridColor ? ' / '+state.subgridColor : ''}`, state.subgridPrice, !!state.subgrid && s >= 8);
    push(`Button — ${state.button}${state.buttonColor ? ' / '+state.buttonColor : ''}`, state.buttonPrice, !!state.button && s >= 9);
    push(`Logo — ${state.logo}`, state.logoPrice, !!state.logo && s >= 10);
    if (state.nameTag !== 'None' && s >= 12) push(`Name Tag — ${state.nameTag}`, state.nameTagPrice, true);

    bd.innerHTML = items.length === 0
      ? '<span class="breakdown-empty">Select options to see price breakdown</span>'
      : items.map(item => {
          const ph = item.isBase ? fmt(item.price) : item.price === 0 ? '<span class="price-included">Included</span>' : `+ ${fmt(item.price)}`;
          return `<div class="price-row${item.price > 0 && !item.isBase ? ' surcharge' : ''}"><span>${item.label}</span><span>${ph}</span></div>`;
        }).join('');
  }
  document.querySelectorAll('.price-total').forEach(el => { el.textContent = fmt(total); });
  const btn = document.getElementById('add-to-cart-btn');
  if (btn) btn.textContent = `Add to cart — ${fmt(total)}`;
  updateOrderSummary(total);
}

function updateOrderSummary(total) {
  const el = document.getElementById('order-summary');
  if (!el) return;
  const frameSplit = getFramePriceSplit();
  const rows = [
    ['Shell',        state.coating && state.shellColor ? `${state.coating} / ${state.shellColor}` : null, state.basePrice, true],
    ['Front Insert', state.frontInsert ? state.frontInsert + (state.frontInsertColor ? ' — '+state.frontInsertColor : '') : null, state.frontInsertPrice, false],
    ['Rear Insert',  state.rearInsert  ? state.rearInsert  + (state.rearInsertColor  ? ' — '+state.rearInsertColor  : '') : null, state.rearInsertPrice, false],
    ['Visor',        state.visorMaterial ? `${state.visorType} / ${state.visorMaterial}${state.visorColor ? ' — '+state.visorColor : ''}` : null, state.visorPrice, false],
    ['Strap',        state.strap ? state.strap + (state.strapColor ? ' — '+state.strapColor : '') : null, state.strapPrice, false],
    ['Frame',        state.frame ? state.frame + (state.frameColor ? ' / '+state.frameColor : '') : null, frameSplit.base, false],
    ['Frame Swarovski', state.frameSwarovski ? (state.frameSwarovskiColor || 'Selected') : null, frameSplit.swarovski, false],
    ['Grid',         state.grid  ? state.grid  + (state.gridColor  ? ' / '+state.gridColor  : '') : null, state.gridPrice,  false],
    ['Subgrid',      state.subgrid ? state.subgrid + (state.subgridColor ? ' / '+state.subgridColor : '') : null, state.subgridPrice, false],
    ['Button',       state.button  ? state.button  + (state.buttonColor  ? ' / '+state.buttonColor  : '') : null, state.buttonPrice,  false],
    ['Logo',         state.logo,   state.logoPrice,    false],
    ['Size',         state.size ? `${state.size}${state.lining ? ' — '+state.lining : ''}` : null, 0, false],
    ['Name Tag',     state.nameTag !== 'None' ? state.nameTag + (state.nameTagText ? ` (${state.nameTagText})` : '') : null, state.nameTagPrice, false],
  ];
  const rowsHtml = rows.map(([lbl, val, price, isBase]) => {
    if (!val) return '';
    const ph = isBase ? fmt(price) : price === 0 ? '<span class="price-included">Included</span>' : `+ ${fmt(price)}`;
    return `<div class="summary-row"><span class="summary-label">${lbl}</span><span class="summary-value">${val}</span><span class="summary-price">${ph}</span></div>`;
  }).join('');
  el.innerHTML = `<h3 class="summary-title">Your configuration</h3>
    <div class="summary-rows">${rowsHtml || '<div style="padding:12px 18px;font-size:13px;color:#888">Complete earlier steps to see your summary.</div>'}</div>
    <div class="summary-total-row"><span>Total</span><span class="price-total">${fmt(total)}</span></div>`;
}

// ─── NAVIGATION ───────────────────────────────────────────────
// ─── STEP DEFAULTS ────────────────────────────────────────────
const STRAP_MATCH_COLORS = ['Black','Blue','Brown','Grey'];

function getStepDefaults() {
  const coating = state.coating;
  const color   = state.shellColor;
  if (!coating || !color) return null;
  const isShine   = coating === 'Shine';
  const isDiamond = coating === 'Diamond';
  const isVelvet  = coating === 'Velvet';

  // Insert: same material + color as shell
  let insertType, insertPrice, insertColor;
  if (isVelvet) {
    const vt = INSERT_TYPES.find(t => t.name === 'Velvet');
    insertType = 'Velvet'; insertPrice = velvetInsertPrice('Velvet', vt ? vt.price : 100); insertColor = 'Black';
  } else {
    const bi = BASIC_INSERT[coating] || BASIC_INSERT['Textile'];
    insertType = coating; insertPrice = bi.price;
    insertColor = bi.colors.includes(color) ? color : bi.colors[0];
  }

  // Visor: Standard, same material + color as shell
  const visorMat   = isVelvet ? 'Velvet' : coating;
  const visorCols  = VISOR_COLORS[visorMat] || [];
  const visorColor = visorCols.includes(color) ? color : (visorCols[0] || null);
  const visorKey   = VISOR_COAT_KEY[visorMat] || 'Textile/Polish/Textured/Shine';
  const visorEntry = VISOR_STD[visorKey] || { default:0, diamond:0 };
  const visorPrice = isDiamond ? visorEntry.diamond
    : (isVelvet && visorEntry.velvet !== undefined) ? visorEntry.velvet : visorEntry.default;

  // Strap: Black/Blue/Brown/Grey shell → same colour leather; Shine → eco leather; other → black leather
  let strap, strapColor;
  if (isShine) {
    strap = 'Eco Leather'; strapColor = (color === 'Blue') ? 'Blue' : 'Black';
  } else if (STRAP_MATCH_COLORS.includes(color)) {
    strap = 'Leather'; strapColor = color;
  } else {
    strap = 'Leather'; strapColor = 'Black';
  }

  // Frame: Mirror Silver for Shine + Textile + Texturized; Matches Shell for all others
  const useMirror = isShine || coating === 'Textile' || coating === 'Texturized';
  const frame      = useMirror ? 'Mirror'        : 'Matches Shell';
  const frameColor = useMirror ? 'Silver'        : null;

  // Grid/Subgrid/Button: Raw for Shine/Texturized/Velvet (these components can't literally be
  // made in those shell finishes); Matches Shell for Polish/Metal/Diamond, mirroring Frame —
  // the dynamic Matches-Shell photo lookup already resolves correctly for those since
  // Polish/Metal/Diamond materials exist under their own name. Textile is conditional: Raw
  // colors (Black/Blue/Grey/Brown) stay Raw in that color; the rest (Turquoise, Golden Sand,
  // Peach, Military/Dark Green) use Matches Shell to get the real Textile-material photo.
  const RAW_COLORS = ['Black','Blue','Grey','Brown'];
  const textileUsesRaw = coating === 'Textile' && RAW_COLORS.includes(color);
  const useRaw = isShine || coating === 'Texturized' || isVelvet || textileUsesRaw;
  const grid    = useRaw ? 'Raw' : 'Matches Shell';
  const subgrid = useRaw ? 'Raw' : 'Matches Shell';
  const button  = useRaw ? 'Raw' : 'Matches Shell';
  const gridColor    = useRaw ? pickDefaultMatColor(GRID_MATS,    'Raw', color) : null;
  const subgridColor = useRaw ? pickDefaultMatColor(SUBGRID_MATS, 'Raw', color) : null;
  const buttonColor  = useRaw ? pickDefaultMatColor(BUTTON_MATS,  'Raw', color) : null;

  return {
    frontInsert: insertType, frontInsertColor: insertColor, frontInsertPrice: insertPrice,
    rearInsert:  insertType, rearInsertColor:  insertColor, rearInsertPrice:  insertPrice,
    visorType: 'Standard',  visorMaterial: visorMat, visorColor, visorPrice,
    strap, strapColor, strapPrice: 0,
    frame, frameColor, framePrice: 0,
    grid,    gridColor,    gridPrice:    0,
    subgrid, subgridColor, subgridPrice: 0,
    button,  buttonColor,  buttonPrice:  0,
    logo: 'Silver Matt', logoPrice: 0,
  };
}

function applyStepDefault(stepNum) {
  const d = getStepDefaults();
  if (!d) return;
  switch (stepNum) {
    case 2:
      if (!state.frontInsert) {
        state.frontInsert = d.frontInsert; state.frontInsertColor = d.frontInsertColor; state.frontInsertPrice = d.frontInsertPrice;
        setStepChoice(2, d.frontInsert + (d.frontInsertColor ? ' / ' + d.frontInsertColor : ''));
      }
      break;
    case 3:
      if (!state.rearInsert) {
        state.rearInsert = d.rearInsert; state.rearInsertColor = d.rearInsertColor; state.rearInsertPrice = d.rearInsertPrice;
        setStepChoice(3, d.rearInsert + (d.rearInsertColor ? ' / ' + d.rearInsertColor : ''));
      }
      break;
    case 4:
      if (!state.visorMaterial) {
        state.visorType = d.visorType; state.visorMaterial = d.visorMaterial; state.visorColor = d.visorColor; state.visorPrice = d.visorPrice;
        setStepChoice(4, d.visorType + ' / ' + d.visorMaterial + (d.visorColor ? ' / ' + d.visorColor : ''));
      }
      break;
    case 5:
      if (!state.strap) {
        state.strap = d.strap; state.strapColor = d.strapColor; state.strapPrice = d.strapPrice; _strapTab = d.strap;
        setStepChoice(5, d.strap + (d.strapColor ? ' / ' + d.strapColor : ''));
        renderStrapOptions();
        const _strapImgMap = STRAP_IMAGES[d.strap] || STRAP_IMAGES['Leather'];
        const _strapImg = (_strapImgMap && d.strapColor && _strapImgMap[d.strapColor]) || null;
        state.stepPreviews[5] = { image: _strapImg, hex: d.strapColor ? h(d.strapColor) : null };
      }
      break;
    case 6:
      if (!state.frame) {
        state.frame = d.frame; state.frameColor = d.frameColor; state.framePrice = d.framePrice;
        setStepChoice(6, d.frame + (d.frameColor ? ' / ' + d.frameColor : ''));
      }
      break;
    case 7:
      if (!state.grid) {
        state.grid = d.grid; state.gridColor = d.gridColor; state.gridPrice = d.gridPrice;
        setStepChoice(7, d.grid + (d.gridColor ? ' / ' + d.gridColor : ''));
      }
      break;
    case 8:
      if (!state.subgrid) {
        state.subgrid = d.subgrid; state.subgridColor = d.subgridColor; state.subgridPrice = d.subgridPrice;
        setStepChoice(8, d.subgrid + (d.subgridColor ? ' / ' + d.subgridColor : ''));
      }
      break;
    case 9:
      if (!state.button) {
        state.button = d.button; state.buttonColor = d.buttonColor; state.buttonPrice = d.buttonPrice;
        setStepChoice(9, d.button + (d.buttonColor ? ' / ' + d.buttonColor : ''));
      }
      break;
    case 10:
      if (!state.logo) {
        state.logo = d.logo; state.logoPrice = d.logoPrice;
        setStepChoice(10, d.logo);
      }
      break;
  }
}

function enableNextIfDefaulted(stepNum) {
  const filled = {
    2: () => !!state.frontInsert,   3: () => !!state.rearInsert,
    4: () => !!state.visorMaterial, 5: () => !!state.strap,
    6: () => !!state.frame,         7: () => !!state.grid,
    8: () => !!state.subgrid,       9: () => !!state.button,
    10: () => !!state.logo,         11: () => !!state.size,
  };
  const check = filled[stepNum];
  if (!check || !check()) return;
  const btn = document.getElementById(`step${stepNum}-next`);
  if (btn) btn.disabled = false;
}

const TOTAL_STEPS = 13;

function showStep(n) {
  if (n > state.currentStep) {
    for (let i = state.currentStep; i < n; i++) applyStepDefault(i);
  }
  document.querySelectorAll('.config-step').forEach(el => el.classList.remove('active'));
  const t = document.getElementById(`step-${n}`);
  if (!t) return;
  t.classList.add('active');
  state.currentStep = n;
  updatePriceDisplay();
  enableNextIfDefaulted(n);
  const layout = document.querySelector('.cromo-layout');
  if (layout) layout.classList.toggle('step-review', n === 13);
  if (n === 13) {
    renderReviewGrid();
    updatePriceDisplay();
  } else {
    const p = state.stepPreviews[n];
    if (p && p.image) {
      setPreview(p.image, p.hex);
    } else if (p && p.hex) {
      setPreview(null, p.hex);
    } else if (n === 1 || !state.coating) {
      setPreview(DEFAULT_PREVIEW_IMAGE, null);
    }
  }
  updateProgress();
  if (_stepScrollEnabled) {
    const siteHeader = document.querySelector('.site-header') || document.querySelector('header');
    if (siteHeader) siteHeader.style.position = 'relative';
    const cromoLayout = document.querySelector('.cromo-layout');
    if (cromoLayout) {
      const top = cromoLayout.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({ top, behavior: 'smooth' });
    }
  }
  _stepScrollEnabled = true;
}

function updateProgress() {
  const pct = (state.currentStep / TOTAL_STEPS) * 100;
  const bar = document.getElementById('page-progress-bar-inner');
  if (bar) bar.style.width = pct + '%';
}

function cleanDependentState() {
  Object.assign(state, {
    frontInsert:null, frontInsertColor:null, frontInsertPrice:0,
    rearInsert:null,  rearInsertColor:null,  rearInsertPrice:0,
    visorType:'Standard', visorMaterial:null, visorColor:'', visorPrice:0,
    strap:null, strapColor:null, strapPrice:0,
    frame:null, frameColor:null, framePrice:0,
    frameSwarovski:false, frameSwarovskiColor:'',
    grid:null,  gridColor:null,  gridPrice:0,
    subgrid:null, subgridColor:null, subgridPrice:0,
    button:null,  buttonColor:null,  buttonPrice:0,
    logo:null, logoPrice:0
  });
  userSelected[2] = false;
  userSelected[3] = false;
  userSelected[4] = false;
  userSelected[5] = false;
  userSelected[6] = false;
  userSelected[7] = false;
  userSelected[8] = false;
  userSelected[9] = false;
  _strapTab = null;
  for (let i = 2; i <= 11; i++) {
    const btn = document.getElementById(`step${i}-next`);
    if (btn) btn.disabled = true;
    setStepChoice(i, '');
    delete state.stepPreviews[i];
  }
}

// ─── STEP 1 — Shell ───────────────────────────────────────────
function buildStep1() {
  const el = document.getElementById('step-1');
  if (!el) return;

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">1</span>
        <h2>Shell<span class="step-choice" id="step-1-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs">
        ${COATING_ORDER.map(c => `<button class="coating-tab" data-coating="${c}">${c}</button>`).join('')}
      </div>
      <div id="shell-colors" class="swatch-grid hidden" style="margin-top:20px;"></div>
      <div id="default-finishes" class="default-finishes hidden"></div>
      <div class="step-nav">
        <button class="btn-next" id="step1-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  el.querySelectorAll('.coating-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      el.querySelectorAll('.coating-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const coating = btn.dataset.coating;
      const prev = state.coating;
      const hadSel = !!(prev && prev !== coating && (state.frontInsert || state.strap || state.frame));
      if (prev && prev !== coating) {
        state.shellColor = null; state.shellColorHex = null;
        cleanDependentState();
      } else {
        state.shellColor = null; state.shellColorHex = null;
        if (!prev) _strapTab = null;
      }
      state.coating   = coating;
      state.basePrice = VARIANTS[coating].basePrice;
      renderShellColors(coating, el);
      renderDefaultFinishes(coating);
      onCoatingChange.forEach(fn => fn());
      updatePriceDisplay();
    });
  });

  el.querySelector('#step1-next').addEventListener('click', () => {
    for (let i = 2; i <= 10; i++) applyStepDefault(i);
    showStep(2);
  });
}

function renderShellColors(coating, stepEl) {
  const grid = document.getElementById('shell-colors');
  grid.innerHTML = '';
  SHELL_COLORS[coating].forEach(name => {
    const sw = makeSwatch(name, h(name));
    sw.addEventListener('click', () => {
      grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
      sw.classList.add('selected');
      state.shellColor    = name;
      state.shellColorHex = h(name);
      state.basePrice = VARIANTS[coating].basePrice;
      // Apply per-color price exception if defined
      const exc = SHELL_PRICE_EXCEPTIONS[coating];
      if (exc && exc[name] !== undefined) state.basePrice = exc[name];
      stepEl.querySelector('#step1-next').disabled = false;
      setStepChoice(1, `${coating} / ${name}`);
      const btn2 = document.getElementById('step2-next');
      const btn3 = document.getElementById('step3-next');
      if (!userSelected[2]) {
        state.frontInsert = null; state.frontInsertColor = null; state.frontInsertPrice = 0;
        delete state.stepPreviews[2]; setStepChoice(2, '');
        if (btn2) btn2.disabled = true;
      }
      if (!userSelected[3]) {
        state.rearInsert = null; state.rearInsertColor = null; state.rearInsertPrice = 0;
        delete state.stepPreviews[3]; setStepChoice(3, '');
        if (btn3) btn3.disabled = true;
      }
      if (!userSelected[4]) {
        state.visorType = 'Standard'; state.visorMaterial = null; state.visorColor = ''; state.visorPrice = 0;
        setStepChoice(4, '');
        const btn4 = document.getElementById('step4-next'); if (btn4) btn4.disabled = true;
      }
      if (!userSelected[5]) {
        state.strap = null; state.strapColor = null; state.strapPrice = 0; _strapTab = null;
        setStepChoice(5, '');
        const btn5 = document.getElementById('step5-next'); if (btn5) btn5.disabled = true;
      }
      const shellImg = (SHELL_IMAGES[coating] || {})[name] || null;
      state.stepPreviews[1] = { image: shellImg, hex: h(name) };
      onCoatingChange.forEach(fn => fn());
      setPreview(shellImg, h(name));
      updatePriceDisplay();
    });
    grid.appendChild(sw);
  });
  grid.classList.remove('hidden');
  grid.querySelector('.swatch').click();
}

function renderDefaultFinishes(coating) {
  const el = document.getElementById('default-finishes');
  const d = DEFAULT_FINISHES[coating];
  el.innerHTML = `<p class="finishes-label">Included standard finishes:</p>
    <ul class="finishes-list">
      <li>Frame: <strong>${d.frame}</strong></li>
      <li>Grid: <strong>${d.grid}</strong></li>
      <li>Subgrid: <strong>${d.subgrid}</strong></li>
      <li>Button: <strong>${d.button}</strong></li>
    </ul>`;
  el.classList.remove('hidden');
}

// ─── STEPS 2 & 3 — Front / Rear Insert ───────────────────────
function buildInsertStep(stepNum, isRear) {
  const el = document.getElementById(`step-${stepNum}`);
  if (!el) return;
  const label = isRear ? 'Rear Insert' : 'Front Insert';

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">${stepNum}</span>
        <h2>${label}<span class="step-choice" id="step-${stepNum}-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs" id="ins-tabs-${stepNum}"></div>
      <div class="swatch-grid hidden" id="ins-swatches-${stepNum}" style="margin-top:20px;padding-bottom:28px;"></div>
      <div class="pill-grid hidden"   id="ins-pills-${stepNum}"   style="margin-top:16px;"></div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step${stepNum}-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  function buildTabs() {
    const bar = document.getElementById(`ins-tabs-${stepNum}`);
    if (!bar) return;
    bar.innerHTML = '';
    BASIC_INSERT_COATINGS.forEach(coating => {
      const btn = document.createElement('button');
      btn.className = 'coating-tab';
      btn.dataset.basicCoating = coating;
      btn.textContent = coating;
      btn.addEventListener('click', () => onBasicTabClick(coating));
      bar.appendChild(btn);
    });
    INSERT_TYPES.forEach(t => {
      if (isRear && t.frontOnly) return;
      const btn = document.createElement('button');
      btn.className = 'coating-tab';
      btn.dataset.typeName = t.name;
      btn.textContent = t.name;
      btn.addEventListener('click', () => onTypeClick(t));
      bar.appendChild(btn);
    });
  }

  function setActiveTab(selector) {
    const bar = document.getElementById(`ins-tabs-${stepNum}`);
    if (!bar) return;
    bar.querySelectorAll('.coating-tab').forEach(b => b.classList.remove('active'));
    const target = bar.querySelector(selector);
    if (target) target.classList.add('active');
  }

  function clearSwatches() {
    const swGrid = document.getElementById(`ins-swatches-${stepNum}`);
    const pills  = document.getElementById(`ins-pills-${stepNum}`);
    if (swGrid) { swGrid.innerHTML = ''; swGrid.classList.add('hidden'); }
    if (pills)  { pills.innerHTML  = ''; pills.classList.add('hidden'); }
    document.getElementById(`step${stepNum}-next`).disabled = true;
  }

  function onBasicTabClick(tabCoating, preferredColor = null) {
    setActiveTab(`[data-basic-coating="${tabCoating}"]`);
    clearSwatches();
    const basic = BASIC_INSERT[tabCoating];
    if (!basic) return;
    const swGrid = document.getElementById(`ins-swatches-${stepNum}`);
    swGrid.classList.remove('hidden');
    basic.colors.forEach(cname => {
      const isFree = tabCoating === state.coating && cname === state.shellColor;
      const sw = makeSwatch(cname, h(cname), { freeBadge: isFree });
      sw.addEventListener('click', () => {
        userSelected[stepNum] = true;
        swGrid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
        sw.classList.add('selected');
        const price = isFree ? 0 : basic.price;
        const colorImg = isRear ? ((basic.rearColorImages && basic.rearColorImages[cname]) || null) : ((basic.colorImages && basic.colorImages[cname]) || null);
        setInsertState('Basic ' + tabCoating, cname, price, colorImg);
      });
      swGrid.appendChild(sw);
    });
    const defaultColor = (preferredColor && basic.colors.includes(preferredColor)) ? preferredColor : basic.colors[0];
    const defaultSw = Array.from(swGrid.querySelectorAll('.swatch')).find(s => s.title === defaultColor) || swGrid.querySelector('.swatch');
    if (defaultSw) {
      defaultSw.classList.add('selected');
      const isFreeDefault = tabCoating === state.coating && defaultColor === state.shellColor;
      const defaultPrice = isFreeDefault ? 0 : basic.price;
      const defaultImg = isRear ? ((basic.rearColorImages && basic.rearColorImages[defaultColor]) || null) : ((basic.colorImages && basic.colorImages[defaultColor]) || null);
      setInsertState('Basic ' + tabCoating, defaultColor, defaultPrice, defaultImg, state.currentStep === stepNum);
    }
  }

  function onTypeClick(typeDef) {
    setActiveTab(`[data-type-name="${typeDef.name}"]`);
    clearSwatches();
    const swGrid = document.getElementById(`ins-swatches-${stepNum}`);
    const pills  = document.getElementById(`ins-pills-${stepNum}`);

    if (typeDef.splitColors) {
      swGrid.classList.remove('hidden');
      typeDef.splitColors.forEach(([c1, c2]) => {
        const label = `${c1} – ${c2}`;
        const colorImg1 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[label]) || null) : ((typeDef.colorImages && typeDef.colorImages[label]) || typeDef.image || null);
        const sw = colorImg1
          ? makeSwatch(label, h(c1), { image: colorImg1 })
          : makeSwatch(label, h(c1), { split: h(c2) });
        sw.title = label;
        sw.addEventListener('click', () => {
          userSelected[stepNum] = true;
          swGrid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          const price = (typeDef.colorPrices && typeDef.colorPrices[label] !== undefined)
            ? typeDef.colorPrices[label] : typeDef.price;
          setInsertState(typeDef.name, label, price, colorImg1);
        });
        swGrid.appendChild(sw);
      });
      const firstSwSplit = swGrid.querySelector('.swatch');
      if (firstSwSplit) {
        firstSwSplit.classList.add('selected');
        const [c1, c2] = typeDef.splitColors[0];
        const label = `${c1} – ${c2}`;
        const price = (typeDef.colorPrices && typeDef.colorPrices[label] !== undefined)
          ? typeDef.colorPrices[label] : typeDef.price;
        const colorImg1 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[label]) || null) : ((typeDef.colorImages && typeDef.colorImages[label]) || typeDef.image || null);
        setInsertState(typeDef.name, label, price, colorImg1, state.currentStep === stepNum);
      }

    } else if (typeDef.pills) {
      pills.classList.remove('hidden');
      typeDef.pills.forEach(pname => {
        const pill = document.createElement('button');
        pill.className = 'color-pill';
        pill.textContent = pname;
        pill.addEventListener('click', () => {
          userSelected[stepNum] = true;
          pills.querySelectorAll('.color-pill').forEach(p => p.classList.remove('active'));
          pill.classList.add('active');
          const price = isRear && typeDef.priceRear !== undefined ? typeDef.priceRear : typeDef.price;
          const colorImg2 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[pname]) || null) : ((typeDef.colorImages && typeDef.colorImages[pname]) || typeDef.image || null);
          setInsertState(typeDef.name, pname, price, colorImg2);
        });
        pills.appendChild(pill);
      });
      const firstPill = pills.querySelector('.color-pill');
      if (firstPill) {
        firstPill.classList.add('active');
        const pname = typeDef.pills[0];
        const price = isRear && typeDef.priceRear !== undefined ? typeDef.priceRear : typeDef.price;
        const colorImg2 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[pname]) || null) : ((typeDef.colorImages && typeDef.colorImages[pname]) || typeDef.image || null);
        setInsertState(typeDef.name, pname, price, colorImg2, state.currentStep === stepNum);
      }

    } else {
      const colors = isRear && typeDef.rearColors ? typeDef.rearColors : (typeDef.colors || []);
      if (colors.length === 0) {
        setInsertState(typeDef.name, null, typeDef.price, isRear ? null : (typeDef.image || null));
        return;
      }
      swGrid.classList.remove('hidden');
      colors.forEach(cname => {
        const sw = makeSwatch(cname, h(cname), { freeBadge: velvetInsertPrice(typeDef.name, 1) === 0 });
        sw.addEventListener('click', () => {
          userSelected[stepNum] = true;
          swGrid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          const basePrice = isRear && typeDef.priceRear !== undefined ? typeDef.priceRear : typeDef.price;
          const price = (!isRear && typeDef.colorPrices && typeDef.colorPrices[cname] !== undefined)
            ? typeDef.colorPrices[cname] : velvetInsertPrice(typeDef.name, basePrice);
          const colorImg3 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[cname]) || null) : (typeDef.colorImages && cname in typeDef.colorImages) ? (typeDef.colorImages[cname] || null) : (typeDef.image || null);
          setInsertState(typeDef.name, cname, price, colorImg3);
        });
        swGrid.appendChild(sw);
      });
      const firstSw = swGrid.querySelector('.swatch');
      if (firstSw) {
        firstSw.classList.add('selected');
        const cname = colors[0];
        const basePrice = isRear && typeDef.priceRear !== undefined ? typeDef.priceRear : typeDef.price;
        const price = (!isRear && typeDef.colorPrices && typeDef.colorPrices[cname] !== undefined)
          ? typeDef.colorPrices[cname] : velvetInsertPrice(typeDef.name, basePrice);
        const colorImg3 = isRear ? ((typeDef.rearColorImages && typeDef.rearColorImages[cname]) || null) : (typeDef.colorImages && cname in typeDef.colorImages) ? (typeDef.colorImages[cname] || null) : (typeDef.image || null);
        setInsertState(typeDef.name, cname, price, colorImg3, state.currentStep === stepNum);
      }
    }
  }

  function setInsertState(typeName, colorName, price, image, applyPreview = true) {
    if (isRear) {
      state.rearInsert      = typeName;
      state.rearInsertColor = colorName;
      state.rearInsertPrice = price;
    } else {
      state.frontInsert      = typeName;
      state.frontInsertColor = colorName;
      state.frontInsertPrice = price;
    }
    setStepChoice(stepNum, typeName + (colorName ? ` / ${colorName}` : ''));
    document.getElementById(`step${stepNum}-next`).disabled = false;
    const previewHex = colorName ? h(colorName) : null;
    state.stepPreviews[stepNum] = { image: image || null, hex: previewHex };
    if (applyPreview) setPreview(image || null, previewHex);
    updatePriceDisplay();
  }

  function refreshOnCoatingChange() {
    if (!userSelected[stepNum] && state.coating && BASIC_INSERT_COATINGS.includes(state.coating)) {
      onBasicTabClick(state.coating, state.shellColor);
    } else if (!userSelected[stepNum] && state.coating === 'Velvet') {
      // Velvet shell: auto-select the Velvet insert tab (Black), like the basic coatings do
      const vt = INSERT_TYPES.find(t => t.name === 'Velvet');
      if (vt) onTypeClick(vt);
    }
    const insert = isRear ? state.rearInsert : state.frontInsert;
    const color  = isRear ? state.rearInsertColor : state.frontInsertColor;
    if (insert && insert.startsWith('Basic ') && state.coating) {
      const tabCoating = insert.replace('Basic ', '');
      const basic = BASIC_INSERT[tabCoating];
      const isFree = tabCoating === state.coating && color === state.shellColor;
      const price = isFree ? 0 : (basic ? basic.price : 0);
      if (isRear) state.rearInsertPrice  = price;
      else        state.frontInsertPrice = price;
      updatePriceDisplay();
    } else if (insert === 'Velvet' && state.coating) {
      const vt = INSERT_TYPES.find(t => t.name === 'Velvet');
      const price = velvetInsertPrice('Velvet', vt ? vt.price : 100);
      if (isRear) state.rearInsertPrice  = price;
      else        state.frontInsertPrice = price;
      updatePriceDisplay();
    }
  }

  onCoatingChange.push(refreshOnCoatingChange);
  buildTabs();

  el.querySelector('.btn-prev').addEventListener('click', () => showStep(stepNum - 1));
  el.querySelector(`#step${stepNum}-next`).addEventListener('click', () => showStep(stepNum + 1));
}

// ─── STEP 4 — Visor ───────────────────────────────────────────
function buildStep4() {
  const el = document.getElementById('step-4');
  if (!el) return;
  const visorCoatings = ['Shine','Textile','Polish','Metal','Diamond','Texturized','Velvet','Leather'];

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">4</span>
        <h2>Visor<span class="step-choice" id="step-4-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="toggle-group" style="margin-bottom:24px;">
        <button class="toggle-btn active" data-vtype="Standard">Standard</button>
        <button class="toggle-btn" data-vtype="Polo">Polo</button>
      </div>
      <div class="coating-tabs" id="visor-mat-tabs">
        ${visorCoatings.map(c => `<button class="coating-tab" data-vmat="${c}">${c}</button>`).join('')}
      </div>
      <div id="visor-color-area" class="swatch-grid hidden" style="margin-top:20px;padding-bottom:28px;"></div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step4-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  function calcVisorPrice() {
    if (!state.visorMaterial || !state.coating) return;
    const matKey = VISOR_COAT_KEY[state.visorMaterial] || 'Textile/Polish/Textured/Shine';
    const isDiamond = state.coating === 'Diamond';
    const isVelvet  = state.coating === 'Velvet';
    const stdEntry  = VISOR_STD[matKey];
    const poloEntry = VISOR_POLO[matKey];
    if (!stdEntry) return;
    const pick = e => isDiamond ? e.diamond
      : (isVelvet && e.velvet !== undefined) ? e.velvet : e.default;
    const std  = pick(stdEntry);
    const polo = state.visorType === 'Polo' ? pick(poloEntry) : 0;
    state.visorPrice = std + polo;
    updatePriceDisplay();
  }

  function selectVisorMaterial(mat, preferredColor = null) {
    el.querySelectorAll('[data-vmat]').forEach(b => b.classList.remove('active'));
    el.querySelector(`[data-vmat="${mat}"]`).classList.add('active');
    state.visorMaterial = mat;
    state.visorColor = '';
    document.getElementById('step4-next').disabled = true;

    const area = document.getElementById('visor-color-area');
    area.innerHTML = '';
    area.classList.add('hidden');

    const colors = VISOR_COLORS[mat] || [];
    if (colors.length === 0) {
      setStepChoice(4, `${state.visorType} / ${mat}`);
      document.getElementById('step4-next').disabled = false;
      state.stepPreviews[4] = { image: null, hex: h(mat) };
      if (state.currentStep === 4) setPreview(null, h(mat));
      calcVisorPrice();
      return;
    }

    area.classList.remove('hidden');
    colors.forEach(cname => {
      const sw = makeSwatch(cname, h(cname));
      sw.addEventListener('click', () => {
        userSelected[4] = true;
        area.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
        sw.classList.add('selected');
        state.visorColor = cname;
        setStepChoice(4, `${state.visorType} / ${mat} / ${cname}`);
        document.getElementById('step4-next').disabled = false;
        const visorImg = (VISOR_IMAGES[state.visorType] && VISOR_IMAGES[state.visorType][mat] && VISOR_IMAGES[state.visorType][mat][cname]) || null;
        state.stepPreviews[4] = { image: visorImg, hex: h(cname) };
        setPreview(visorImg, h(cname));
        calcVisorPrice();
      });
      area.appendChild(sw);
    });

    // Auto-select default color
    const defaultColor = (preferredColor && colors.includes(preferredColor)) ? preferredColor : colors[0];
    const defaultSw = Array.from(area.querySelectorAll('.swatch')).find(s => s.title === defaultColor) || area.querySelector('.swatch');
    if (defaultSw) {
      defaultSw.classList.add('selected');
      state.visorColor = defaultColor;
      setStepChoice(4, `${state.visorType} / ${mat} / ${defaultColor}`);
      document.getElementById('step4-next').disabled = false;
      const visorImg = (VISOR_IMAGES[state.visorType] && VISOR_IMAGES[state.visorType][mat] && VISOR_IMAGES[state.visorType][mat][defaultColor]) || null;
      state.stepPreviews[4] = { image: visorImg, hex: h(defaultColor) };
      if (state.currentStep === 4) setPreview(visorImg, h(defaultColor));
      calcVisorPrice();
    }
  }

  el.querySelectorAll('.toggle-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      userSelected[4] = true;
      el.querySelectorAll('.toggle-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.visorType = btn.dataset.vtype;
      if (state.visorMaterial) {
        selectVisorMaterial(state.visorMaterial, state.visorColor);
      }
    });
  });

  el.querySelectorAll('[data-vmat]').forEach(btn => {
    btn.addEventListener('click', () => { userSelected[4] = true; selectVisorMaterial(btn.dataset.vmat); });
  });

  onCoatingChange.push(() => {
    if (!userSelected[4] && state.coating && state.shellColor) {
      const d = getStepDefaults();
      if (d) selectVisorMaterial(d.visorMaterial, d.visorColor);
    } else if (state.visorMaterial) {
      calcVisorPrice();
    }
  });

  el.querySelector('.btn-prev').addEventListener('click', () => showStep(3));
  el.querySelector('#step4-next').addEventListener('click', () => showStep(5));
}

// ─── STEP 5 — Strap ───────────────────────────────────────────
function buildStep5() {
  const el = document.getElementById('step-5');
  if (!el) return;
  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">5</span>
        <h2>Strap<span class="step-choice" id="step-5-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div id="strap-options"></div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step5-next" disabled>Next &rarr;</button>
      </div>
    </div>`;
  onCoatingChange.push(renderStrapOptions);
  renderStrapOptions();
  el.querySelector('.btn-prev').addEventListener('click', () => showStep(4));
  el.querySelector('#step5-next').addEventListener('click', () => showStep(6));
}

function renderStrapOptions() {
  const container = document.getElementById('strap-options');
  if (!container) return;

  const isShine = state.coating === 'Shine';

  const tabs = isShine
    ? ['Eco Leather', 'Leather', 'Swarovski', 'Snake']
    : ['Leather',     'Swarovski', 'Snake'];

  if (state.strap === 'Eco Leather' && !isShine) {
    state.strap = null; state.strapColor = null; state.strapPrice = 0;
    _strapTab = null;
    const nb = document.getElementById('step5-next');
    if (nb) nb.disabled = true;
  }

  if (!_strapTab || !tabs.includes(_strapTab)) _strapTab = tabs[0];

  const tabsHtml = tabs.map(t =>
    `<button class="coating-tab${_strapTab === t ? ' active' : ''}" data-strap-tab="${t}">${t}</button>`
  ).join('');

  const leatherStdPrice  = isShine ? 150 : 0;
  const leatherExtPrice  = isShine ? 170 : 20;
  const swarovskiPrice   = isShine ? 475 : 410;
  const snakePrice       = isShine ? 385 : 320;

  // Recalculate price if coating changed and a strap is already selected
  if (state.strap === 'Leather' && state.strapColor) {
    state.strapPrice = STRAP_LEATHER_EXT.includes(state.strapColor) ? leatherExtPrice : leatherStdPrice;
  } else if (state.strap === 'Swarovski') {
    state.strapPrice = swarovskiPrice;
  } else if (state.strap === 'Snake') {
    state.strapPrice = snakePrice;
  }

  let swatchesHtml = '';
  if (_strapTab === 'Eco Leather') {
    swatchesHtml = ECO_LEATHER_COLORS.map(c => strapSwHtml(c, h(c), 'Eco Leather', c, 0,
      state.strap === 'Eco Leather' && state.strapColor === c)).join('');
  } else if (_strapTab === 'Leather') {
    const stdH = STRAP_LEATHER_STD.map(c => strapSwHtml(c, h(c), 'Leather', c, leatherStdPrice,
      state.strap === 'Leather' && state.strapColor === c)).join('');
    const extH = STRAP_LEATHER_EXT.map(c => strapSwHtml(c, h(c), 'Leather', c, leatherExtPrice,
      state.strap === 'Leather' && state.strapColor === c)).join('');
    swatchesHtml = stdH + extH;
  } else if (_strapTab === 'Swarovski') {
    swatchesHtml = SWAROVSKI_STRAP.combos.map(([c1, c2]) => {
      const label = `${c1} – ${c2}`;
      return strapSwHtml(label, `linear-gradient(135deg,${h(c1)} 50%,${h(c2)} 50%)`,
        'Swarovski', label, swarovskiPrice,
        state.strap === 'Swarovski' && state.strapColor === label, true);
    }).join('');
  } else if (_strapTab === 'Snake') {
    swatchesHtml = SNAKE_COLORS.map(c => strapSwHtml(c, h(c), 'Snake', c, snakePrice,
      state.strap === 'Snake' && state.strapColor === c)).join('');
  }

  container.innerHTML = `
    <div class="coating-tabs">${tabsHtml}</div>
    <div class="swatch-grid" style="margin-top:20px;padding-bottom:28px;">${swatchesHtml}</div>`;

  container.querySelectorAll('[data-strap-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      _strapTab = btn.dataset.strapTab;
      renderStrapOptions();
      const firstSw = container.querySelector('[data-strap-type]');
      if (firstSw) firstSw.click();
    });
  });

  container.querySelectorAll('[data-strap-type]').forEach(btn => {
    btn.addEventListener('click', () => {
      userSelected[5] = true;
      state.strap      = btn.dataset.strapType;
      state.strapColor = btn.dataset.strapColor || null;
      state.strapPrice = parseFloat(btn.dataset.strapPrice) || 0;
      _strapTab = state.strap;
      setStepChoice(5, state.strapColor ? `${state.strap} / ${state.strapColor}` : state.strap);
      const nb = document.getElementById('step5-next');
      if (nb) nb.disabled = false;
      const strapHexColor = state.strapColor ? state.strapColor.split(' – ')[0].trim() : state.strap;
      const strapImgMap = STRAP_IMAGES[state.strap] || STRAP_IMAGES['Leather'];
      const strapImg = (strapImgMap && state.strapColor && strapImgMap[state.strapColor]) || null;
      state.stepPreviews[5] = { image: strapImg, hex: h(strapHexColor) };
      setPreview(strapImg, h(strapHexColor));
      updatePriceDisplay();
      renderStrapOptions();
    });
  });
}

// HTML string helper for strap swatches (used with innerHTML)
function strapSwHtml(title, bg, type, color, price, selected, isSplit) {
  return `<button class="swatch${isSplit ? ' swatch-split' : ''}${selected ? ' selected' : ''}"
    data-strap-type="${type}" data-strap-color="${color.replace(/"/g,'&quot;')}" data-strap-price="${price}"
    style="background:${bg};" title="${title}">
    <span class="checkmark">&#10003;</span>
  </button>`;
}

// ─── STEP 6 — Frame ───────────────────────────────────────────
function buildStep6() {
  const el = document.getElementById('step-6');
  if (!el) return;

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">6</span>
        <h2>Frame<span class="step-choice" id="step-6-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs" id="frame-mat-tabs">
        ${FRAME_MATS.map(m => `<button class="coating-tab" data-mat="${m.name}">${m.name}</button>`).join('')}
      </div>
      <div class="swatch-grid hidden" id="frame-sw" style="margin-top:20px;padding-bottom:16px;"></div>
      <div id="frame-swarovski-section" class="frame-swarovski-section hidden">
        <div class="swarovski-question">Add Swarovski stones to frame?</div>
        <div class="coating-tabs" style="margin-top:10px;">
          <button class="coating-tab" id="frame-sw-no">No</button>
          <button class="coating-tab" id="frame-sw-yes">Yes</button>
        </div>
        <div id="frame-sw-color-wrap" class="hidden" style="margin-top:12px;">
          <div class="swatch-grid" id="frame-sw-crystal-grid"></div>
        </div>
      </div>
      <div class="step-nav">
        <button class="btn-prev" id="s6-back">&larr; Back</button>
        <button class="btn-next" id="step6-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  function calcFramePrice(mat, colorName) {
    if (state.frameSwarovski) return state.coating === 'Diamond' ? 285 : 300;
    return getColorPrice(mat, colorName, state.coating);
  }

  function commitFrameSelection(matName, colorName) {
    const mat = FRAME_MATS.find(m => m.name === matName);
    if (!mat) return;
    state.frame = matName;
    state.frameColor = colorName || null;
    state.framePrice = calcFramePrice(mat, colorName);
    const base = colorName ? `${matName} / ${colorName}` : matName;
    setStepChoice(6, base + (state.frameSwarovski ? ' + Swarovski' : ''));
    document.getElementById('step6-next').disabled = false;
    const frameHex = colorName ? h(colorName) : (mat.matchesShell ? state.shellColorHex : null);
    const frameImg = (mat.colorImages && colorName ? mat.colorImages[colorName] : null)
      || (mat.matchesShell ? getMatchesShellImage(FRAME_MATS) : null)
      || mat.image || null;
    state.stepPreviews[6] = { image: frameImg, hex: frameHex };
    if (state.currentStep === 6) setPreview(frameImg, frameHex);
    updatePriceDisplay();
  }

  function populateCrystalGrid() {
    const grid = document.getElementById('frame-sw-crystal-grid');
    if (!grid) return;
    grid.innerHTML = '';
    FRAME_SWAROVSKI_COLORS.forEach(([cname, chex, cimg]) => {
      const sw = makeSwatch(cname, chex, { image: cimg });
      if (state.frameSwarovskiColor === cname) sw.classList.add('selected');
      sw.addEventListener('click', () => {
        grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
        sw.classList.add('selected');
        state.frameSwarovskiColor = cname;
        updatePriceDisplay();
      });
      grid.appendChild(sw);
    });
  }

  el.querySelectorAll('#frame-mat-tabs .coating-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      userSelected[6] = true;
      el.querySelectorAll('#frame-mat-tabs .coating-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const matName = btn.dataset.mat;
      const mat = FRAME_MATS.find(m => m.name === matName);
      if (!mat) return;

      const swPan = document.getElementById('frame-sw');
      swPan.innerHTML = '';
      swPan.classList.add('hidden');
      state.frameSwarovski = false;
      state.frameSwarovskiColor = '';
      const swSect = document.getElementById('frame-swarovski-section');
      swSect.classList.add('hidden');
      el.querySelector('#frame-sw-no').classList.remove('active');
      el.querySelector('#frame-sw-yes').classList.remove('active');
      document.getElementById('frame-sw-color-wrap').classList.add('hidden');
      document.getElementById('step6-next').disabled = true;

      if (mat.matchesShell || !mat.colors || mat.colors.length === 0) {
        commitFrameSelection(matName, null);
        swSect.classList.remove('hidden');
      } else if (mat.colors.length === 1) {
        const [[cname, chex]] = mat.colors;
        swPan.classList.remove('hidden');
        const sw = makeSwatch(cname, chex);
        sw.classList.add('selected');
        sw.addEventListener('click', () => {
          swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          commitFrameSelection(matName, cname);
        });
        swPan.appendChild(sw);
        commitFrameSelection(matName, cname);
        swSect.classList.remove('hidden');
      } else {
        swPan.classList.remove('hidden');
        mat.colors.forEach(([cname, chex]) => {
          const sw = makeSwatch(cname, chex);
          sw.addEventListener('click', () => {
            swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
            sw.classList.add('selected');
            commitFrameSelection(matName, cname);
            swSect.classList.remove('hidden');
          });
          swPan.appendChild(sw);
        });
        const firstSw = swPan.querySelector('.swatch');
        if (firstSw) firstSw.click();
      }
    });
  });

  el.querySelector('#frame-sw-no').addEventListener('click', () => {
    el.querySelector('#frame-sw-no').classList.add('active');
    el.querySelector('#frame-sw-yes').classList.remove('active');
    state.frameSwarovski = false;
    state.frameSwarovskiColor = '';
    document.getElementById('frame-sw-color-wrap').classList.add('hidden');
    if (state.frame) {
      const mat = FRAME_MATS.find(m => m.name === state.frame);
      if (mat) state.framePrice = getColorPrice(mat, state.frameColor, state.coating);
      const base = state.frameColor ? `${state.frame} / ${state.frameColor}` : state.frame;
      setStepChoice(6, base);
    }
    updatePriceDisplay();
  });

  el.querySelector('#frame-sw-yes').addEventListener('click', () => {
    el.querySelector('#frame-sw-yes').classList.add('active');
    el.querySelector('#frame-sw-no').classList.remove('active');
    state.frameSwarovski = true;
    state.framePrice = state.coating === 'Diamond' ? 285 : 300;
    if (!state.frameSwarovskiColor) state.frameSwarovskiColor = 'Crystal';
    document.getElementById('frame-sw-color-wrap').classList.remove('hidden');
    populateCrystalGrid();
    if (state.frame) {
      const base = state.frameColor ? `${state.frame} / ${state.frameColor}` : state.frame;
      setStepChoice(6, base + ' + Swarovski');
    }
    updatePriceDisplay();
  });

  function selectFrameDefault(matName, colorName) {
    const mat = FRAME_MATS.find(m => m.name === matName);
    if (!mat) return;
    el.querySelectorAll('#frame-mat-tabs .coating-tab').forEach(b => b.classList.remove('active'));
    const tab = el.querySelector(`#frame-mat-tabs [data-mat="${matName}"]`);
    if (tab) tab.classList.add('active');
    const swPan = document.getElementById('frame-sw');
    const swSect = document.getElementById('frame-swarovski-section');
    swPan.innerHTML = '';
    swPan.classList.add('hidden');
    state.frameSwarovski = false;
    state.frameSwarovskiColor = '';
    if (swSect) {
      swSect.classList.add('hidden');
      el.querySelector('#frame-sw-no').classList.remove('active');
      el.querySelector('#frame-sw-yes').classList.remove('active');
      document.getElementById('frame-sw-color-wrap').classList.add('hidden');
    }
    document.getElementById('step6-next').disabled = true;
    if (mat.matchesShell || !mat.colors || mat.colors.length === 0) {
      commitFrameSelection(matName, null);
      if (swSect) swSect.classList.remove('hidden');
    } else if (mat.colors.length === 1) {
      const [[cname, chex]] = mat.colors;
      const sw = makeSwatch(cname, chex);
      sw.classList.add('selected');
      sw.addEventListener('click', () => {
        swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
        sw.classList.add('selected');
        commitFrameSelection(matName, cname);
      });
      swPan.classList.remove('hidden');
      swPan.appendChild(sw);
      commitFrameSelection(matName, cname);
      if (swSect) swSect.classList.remove('hidden');
    } else {
      swPan.classList.remove('hidden');
      mat.colors.forEach(([cname, chex]) => {
        const sw = makeSwatch(cname, chex);
        sw.addEventListener('click', () => {
          swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          commitFrameSelection(matName, cname);
          if (swSect) swSect.classList.remove('hidden');
        });
        swPan.appendChild(sw);
      });
      const targetColor = colorName || (mat.colors[0] && mat.colors[0][0]);
      const targetSw = Array.from(swPan.querySelectorAll('.swatch')).find(s => s.title === targetColor) || swPan.querySelector('.swatch');
      if (targetSw) {
        targetSw.classList.add('selected');
        commitFrameSelection(matName, targetSw.title);
        if (swSect) swSect.classList.remove('hidden');
      }
    }
  }

  onCoatingChange.push(() => {
    if (!userSelected[6] && state.coating && state.shellColor) {
      const d = getStepDefaults();
      if (d) selectFrameDefault(d.frame, d.frameColor);
    } else if (state.frame) {
      const mat = FRAME_MATS.find(m => m.name === state.frame);
      if (mat) {
        state.framePrice = state.frameSwarovski
          ? (state.coating === 'Diamond' ? 285 : 300)
          : getColorPrice(mat, state.frameColor, state.coating);
        updatePriceDisplay();
      }
    }
  });

  el.querySelector('#s6-back').addEventListener('click', () => showStep(5));
  el.querySelector('#step6-next').addEventListener('click', () => showStep(7));
}

// ─── STEPS 7–9 — Grid / Subgrid / Button ─────────────────────
function buildComponentStep(cfg) {
  const el = document.getElementById(`step-${cfg.num}`);
  if (!el) return;

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">${cfg.num}</span>
        <h2>${cfg.label}<span class="step-choice" id="step-${cfg.num}-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs" id="comp-tabs-${cfg.num}">
        ${cfg.mats.map(m => `<button class="coating-tab" data-mat="${m.name}">${m.label || m.name}</button>`).join('')}
      </div>
      <div class="swatch-grid hidden" id="comp-sw-${cfg.num}" style="margin-top:20px;padding-bottom:28px;"></div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step${cfg.num}-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  function selectMat(matName, colorName) {
    const mat = cfg.mats.find(m => m.name === matName);
    if (!mat) return;
    state[cfg.stateKey]  = matName;
    state[cfg.colorKey]  = colorName || null;
    state[cfg.priceKey]  = getColorPrice(mat, colorName, state.coating);
    const choiceTxt = colorName ? `${mat.label || matName} / ${colorName}` : (mat.label || matName);
    setStepChoice(cfg.num, choiceTxt);
    document.getElementById(`step${cfg.num}-next`).disabled = false;
    const compHex = colorName ? h(colorName) : (mat.matchesShell ? state.shellColorHex : null);
    const compImg = (mat.colorImages && colorName ? mat.colorImages[colorName] : null)
      || (mat.matchesShell ? getMatchesShellImage(cfg.mats) : null)
      || mat.image || null;
    state.stepPreviews[cfg.num] = { image: compImg, hex: compHex };
    if (state.currentStep === cfg.num) setPreview(compImg, compHex);
    updatePriceDisplay();
  }

  el.querySelectorAll('.coating-tab').forEach(btn => {
    btn.addEventListener('click', () => {
      userSelected[cfg.num] = true;
      el.querySelectorAll('.coating-tab').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      const matName = btn.dataset.mat;
      const mat     = cfg.mats.find(m => m.name === matName);
      if (!mat) return;

      const swPan = document.getElementById(`comp-sw-${cfg.num}`);
      swPan.innerHTML = '';
      swPan.classList.add('hidden');
      document.getElementById(`step${cfg.num}-next`).disabled = true;

      if (mat.matchesShell) {
        selectMat(matName, null);
      } else if (!mat.colors || mat.colors.length === 0) {
        selectMat(matName, null);
      } else if (mat.colors.length === 1) {
        selectMat(matName, mat.colors[0][0]);
      } else {
        swPan.classList.remove('hidden');
        mat.colors.forEach(([cname, chex]) => {
          const sw = makeSwatch(cname, chex);
          sw.addEventListener('click', () => {
            swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
            sw.classList.add('selected');
            selectMat(matName, cname);
          });
          swPan.appendChild(sw);
        });
        const firstSw = swPan.querySelector('.swatch');
        if (firstSw) firstSw.click();
      }
    });
  });

  function selectCompDefault(matName, colorName) {
    el.querySelectorAll('.coating-tab').forEach(b => b.classList.remove('active'));
    const tab = el.querySelector(`[data-mat="${matName}"]`);
    if (tab) tab.classList.add('active');
    const mat = cfg.mats.find(m => m.name === matName);
    if (!mat) return;
    const swPan = document.getElementById(`comp-sw-${cfg.num}`);
    swPan.innerHTML = '';
    swPan.classList.add('hidden');
    document.getElementById(`step${cfg.num}-next`).disabled = true;
    if (mat.matchesShell || !mat.colors || mat.colors.length === 0) {
      selectMat(matName, null);
    } else if (mat.colors.length === 1) {
      selectMat(matName, mat.colors[0][0]);
    } else {
      swPan.classList.remove('hidden');
      mat.colors.forEach(([cname, chex]) => {
        const sw = makeSwatch(cname, chex);
        sw.addEventListener('click', () => {
          swPan.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          selectMat(matName, cname);
        });
        swPan.appendChild(sw);
      });
      const targetColor = colorName || (mat.colors[0] && mat.colors[0][0]);
      const targetSw = Array.from(swPan.querySelectorAll('.swatch')).find(s => s.title === targetColor) || swPan.querySelector('.swatch');
      if (targetSw) { targetSw.classList.add('selected'); selectMat(matName, targetSw.title); }
    }
  }

  onCoatingChange.push(() => {
    if (!userSelected[cfg.num] && state.coating && state.shellColor) {
      const d = getStepDefaults();
      if (d) selectCompDefault(d[cfg.stateKey], d[cfg.colorKey]);
    } else if (state[cfg.stateKey]) {
      const mat = cfg.mats.find(m => m.name === state[cfg.stateKey]);
      if (mat) { state[cfg.priceKey] = getColorPrice(mat, state[cfg.colorKey], state.coating); updatePriceDisplay(); }
    }
  });

  el.querySelector('.btn-prev').addEventListener('click', () => showStep(cfg.prev));
  el.querySelector(`#step${cfg.num}-next`).addEventListener('click', () => showStep(cfg.next));
}

// ─── STEP 10 — Logo ───────────────────────────────────────────
function buildStep10() {
  const el = document.getElementById('step-10');
  if (!el) return;
  const logoTabs = ['Metallic','Zircoon','Flag Swarovski','Crystal'];

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">10</span>
        <h2>Logo<span class="step-choice" id="step-10-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs" id="logo-tabs">
        ${logoTabs.map(t => `<button class="coating-tab" data-ltype="${t}">${t}</button>`).join('')}
      </div>
      <div id="logo-content" style="margin-top:20px;"></div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step10-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  function setLogoChoice(name, price, image, hex) {
    state.logo      = name;
    state.logoPrice = price;
    setStepChoice(10, name);
    document.getElementById('step10-next').disabled = false;
    state.stepPreviews[10] = { image: image || null, hex: hex || null };
    if (state.currentStep === 10) setPreview(image || null, hex || null);
    updatePriceDisplay();
  }

  function renderLogoType(typeName) {
    const content = document.getElementById('logo-content');
    content.innerHTML = '';
    document.getElementById('step10-next').disabled = true;

    if (typeName === 'Metallic') {
      const grid = document.createElement('div');
      grid.className = 'swatch-grid';
      LOGO_METALLIC.forEach(c => {
        const sw = makeSwatch(c.name, c.top, { split: c.bottom, splitDir: '180deg' });
        sw.addEventListener('click', () => {
          grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          setLogoChoice(c.name, c.price, c.image || null, c.top);
        });
        grid.appendChild(sw);
      });
      content.appendChild(grid);

    } else if (typeName === 'Zircoon') {
      const grid = document.createElement('div');
      grid.className = 'swatch-grid';
      LOGO_ZIRCOON.forEach(c => {
        const sw = makeSwatch(c.name, c.top, { split: c.bottom, splitDir: '180deg' });
        sw.addEventListener('click', () => {
          grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          setLogoChoice(c.name, c.price, c.image || null, c.top);
        });
        grid.appendChild(sw);
      });
      content.appendChild(grid);

    } else if (typeName === 'Flag Swarovski') {
      const grid = document.createElement('div');
      grid.className = 'swatch-grid';
      LOGO_FLAGS.forEach(f => {
        const sw = makeFlagSwatch(f.name, f.code);
        sw.addEventListener('click', () => {
          grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
          sw.classList.add('selected');
          setLogoChoice(f.name, f.price, f.image || `https://flagcdn.com/w160/${f.code}.png`, null);
        });
        grid.appendChild(sw);
      });
      content.appendChild(grid);

    } else if (typeName === 'Crystal') {
      const grid = document.createElement('div');
      grid.className = 'swatch-grid';
      const sw = makeSwatch(LOGO_CRYSTAL.name, LOGO_CRYSTAL.top, { split: LOGO_CRYSTAL.bottom, splitDir: '180deg' });
      sw.addEventListener('click', () => {
        grid.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
        sw.classList.add('selected');
        setLogoChoice(LOGO_CRYSTAL.name, LOGO_CRYSTAL.price, LOGO_CRYSTAL.image, LOGO_CRYSTAL.top);
      });
      grid.appendChild(sw);
      content.appendChild(grid);
    }
  }

  el.querySelectorAll('[data-ltype]').forEach(tab => {
    tab.addEventListener('click', () => {
      el.querySelectorAll('[data-ltype]').forEach(t => t.classList.remove('active'));
      tab.classList.add('active');
      state.logo = null; state.logoPrice = 0;
      renderLogoType(tab.dataset.ltype);
      const firstSw = document.getElementById('logo-content').querySelector('.swatch');
      if (firstSw) firstSw.click();
    });
  });

  onCoatingChange.push(() => {
    if (!state.logo && state.coating && state.shellColor) {
      const d = getStepDefaults();
      if (!d || !d.logo) return;
      const metalTab = el.querySelector('[data-ltype="Metallic"]');
      if (metalTab) {
        el.querySelectorAll('[data-ltype]').forEach(t => t.classList.remove('active'));
        metalTab.classList.add('active');
        state.logo = null; state.logoPrice = 0;
        renderLogoType('Metallic');
        const content = document.getElementById('logo-content');
        if (content) {
          const targetSw = Array.from(content.querySelectorAll('.swatch')).find(s => s.title === d.logo) || content.querySelector('.swatch');
          if (targetSw) {
            content.querySelectorAll('.swatch').forEach(s => s.classList.remove('selected'));
            targetSw.classList.add('selected');
            const target = LOGO_METALLIC.find(c => c.name === d.logo) || LOGO_METALLIC[0];
            if (target) setLogoChoice(target.name, target.price, target.image || null, target.top);
          }
        }
      }
    }
  });

  el.querySelector('.btn-prev').addEventListener('click', () => showStep(9));
  el.querySelector('#step10-next').addEventListener('click', () => showStep(11));
}

// ─── STEP 11 — Size ───────────────────────────────────────────
function buildStep11() {
  const el = document.getElementById('step-11');
  if (!el) return;
  const eu = Array.from({length:12}, (_,i) => 51+i);
  const us = eu.map(n => n + 'A');
  const btnRow = arr => arr.map(s => `<button class="size-btn" data-size="${s}">${s}</button>`).join('');

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">11</span>
        <h2>Size<span class="step-choice" id="step-11-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="size-scale-label">European (51–62)</div>
      <div class="size-btn-row">${btnRow(eu)}</div>
      <div class="size-scale-label" style="margin-top:16px;">American / Narrow (51A–62A)</div>
      <div class="size-btn-row">${btnRow(us)}</div>
      <div class="step-nav">
        <button class="btn-prev">&larr; Back</button>
        <button class="btn-next" id="step11-next" disabled>Next &rarr;</button>
      </div>
    </div>`;

  el.querySelectorAll('.size-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      el.querySelectorAll('.size-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.size   = btn.dataset.size;
      state.lining = state.size.endsWith('A') ? 'Narrow' : 'Oval / Round';
      setStepChoice(11, `${state.size} — ${state.lining}`);
      document.getElementById('step11-next').disabled = false;
      updatePriceDisplay();
    });
  });

  el.querySelector('.btn-prev').addEventListener('click', () => showStep(10));
  el.querySelector('#step11-next').addEventListener('click', () => showStep(12));
}

// ─── STEP 12 — Name Tag ───────────────────────────────────────
function buildStep12() {
  const el = document.getElementById('step-12');
  if (!el) return;

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">12</span>
        <h2>Name Tag <span class="optional-badge">Optional</span><span class="step-choice" id="step-12-choice"></span></h2>
      </div>
    </div>
    <div class="step-body">
      <div class="coating-tabs" style="margin-bottom:0;">
        ${NAMETAG_OPTIONS.map(o =>
          `<button class="coating-tab s12-nt-btn${o.name === 'None' ? ' active' : ''}" data-nt="${o.name}" data-price="${o.price}" data-image="${o.image || ''}">${o.name}</button>`
        ).join('')}
      </div>
      <div class="nametag-input-wrap" id="nt-input-wrap">
        <div class="form-group" style="margin-top:16px;">
          <label for="nt-name-inp">Name to engrave</label>
          <input type="text" id="nt-name-inp" placeholder="Enter the name for your helmet tag" maxlength="40">
        </div>
      </div>
      <div id="validation-errors" class="validation-errors hidden"></div>
      <div class="step-nav">
        <button class="btn-prev" id="s12-back">&larr; Back</button>
        <button class="btn-next" id="step12-next">Review order &rarr;</button>
      </div>
    </div>`;

  el.querySelectorAll('.s12-nt-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      el.querySelectorAll('.s12-nt-btn').forEach(b => b.classList.remove('active'));
      btn.classList.add('active');
      state.nameTag      = btn.dataset.nt;
      state.nameTagPrice = parseFloat(btn.dataset.price) || 0;
      setStepChoice(12, state.nameTag === 'None' ? '' : state.nameTag);
      const img = btn.dataset.image || null;
      state.stepPreviews[12] = { image: img || null, hex: null };
      if (img) setPreview(img, null);
      const wrap = document.getElementById('nt-input-wrap');
      if (state.nameTag !== 'None') {
        wrap.classList.add('visible');
      } else {
        wrap.classList.remove('visible');
        state.nameTagText = '';
        const i = document.getElementById('nt-name-inp');
        if (i) i.value = '';
      }
      updatePriceDisplay();
    });
  });

  el.querySelector('#nt-name-inp').addEventListener('input', e => {
    state.nameTagText = e.target.value;
    updatePriceDisplay();
  });

  el.querySelector('#s12-back').addEventListener('click', () => showStep(11));

  el.querySelector('#step12-next').addEventListener('click', () => {
    document.getElementById('validation-errors').classList.add('hidden');
    if (!validateAll()) { showValidationErrors(); return; }
    showStep(13);
  });
}

// ─── REVIEW GRID ──────────────────────────────────────────────
function renderReviewGrid() {
  const grid = document.getElementById('review-grid');
  if (!grid) return;

  // Returns { image, hex } for a step — uses stored preview first, then derives from state
  function resolveVisual(n) {
    const s = state.stepPreviews[n] || {};
    if (s.image || s.hex) return { image: s.image || null, hex: s.hex || null };
    switch (n) {
      case 1: {
        const img = (SHELL_IMAGES[state.coating] || {})[state.shellColor] || null;
        return { image: img, hex: state.shellColor ? h(state.shellColor) : null };
      }
      case 2: {
        const coat = state.frontInsert ? state.frontInsert.replace('Basic ', '') : null;
        const bi   = coat ? BASIC_INSERT[coat] : null;
        if (bi) {
          const img = bi.colorImages && state.frontInsertColor ? (bi.colorImages[state.frontInsertColor] || null) : null;
          return { image: img, hex: state.frontInsertColor ? h(state.frontInsertColor) : null };
        }
        const it = INSERT_TYPES.find(t => t.name === state.frontInsert);
        const img2 = it && it.colorImages && state.frontInsertColor ? (it.colorImages[state.frontInsertColor] || it.image || null) : (it && it.image || null);
        return { image: img2, hex: state.frontInsertColor ? h(state.frontInsertColor.split(' – ')[0].trim()) : null };
      }
      case 3: {
        const coat = state.rearInsert ? state.rearInsert.replace('Basic ', '') : null;
        const bi   = coat ? BASIC_INSERT[coat] : null;
        if (bi) {
          const img = bi.rearColorImages && state.rearInsertColor ? (bi.rearColorImages[state.rearInsertColor] || null) : null;
          return { image: img, hex: state.rearInsertColor ? h(state.rearInsertColor) : null };
        }
        const it = INSERT_TYPES.find(t => t.name === state.rearInsert);
        const img2 = it && it.rearColorImages && state.rearInsertColor ? (it.rearColorImages[state.rearInsertColor] || null) : (it && it.image || null);
        return { image: img2, hex: state.rearInsertColor ? h(state.rearInsertColor.split(' – ')[0].trim()) : null };
      }
      case 4: {
        const vImages = VISOR_IMAGES[state.visorType] || {};
        const matImg  = (vImages[state.visorMaterial] || {})[state.visorColor] || null;
        return { image: matImg, hex: state.visorColor ? h(state.visorColor) : null };
      }
      case 5: {
        const map = STRAP_IMAGES[state.strap] || STRAP_IMAGES['Leather'];
        return { image: (map && state.strapColor && map[state.strapColor]) || null, hex: state.strapColor ? h(state.strapColor) : null };
      }
      case 6: {
        const mat = FRAME_MATS.find(m => m.name === state.frame);
        const hex6 = state.frameColor ? h(state.frameColor) : (mat && mat.matchesShell ? h(state.shellColor) : null);
        const img6 = (mat && mat.colorImages && state.frameColor ? mat.colorImages[state.frameColor] : null)
          || (mat && mat.matchesShell ? getMatchesShellImage(FRAME_MATS) : null)
          || (mat && mat.image) || null;
        return { image: img6, hex: hex6 };
      }
      case 7: {
        const mat = GRID_MATS.find(m => m.name === state.grid);
        const hex7 = state.gridColor ? h(state.gridColor) : h(state.shellColor);
        const img7 = (mat && mat.colorImages && state.gridColor ? mat.colorImages[state.gridColor] : null)
          || (mat && mat.matchesShell ? getMatchesShellImage(GRID_MATS) : null)
          || (mat && mat.image) || null;
        return { image: img7, hex: hex7 };
      }
      case 8: {
        const mat = SUBGRID_MATS.find(m => m.name === state.subgrid);
        const hex8 = state.subgridColor ? h(state.subgridColor) : h(state.shellColor);
        const img8 = (mat && mat.colorImages && state.subgridColor ? mat.colorImages[state.subgridColor] : null)
          || (mat && mat.matchesShell ? getMatchesShellImage(SUBGRID_MATS) : null)
          || (mat && mat.image) || null;
        return { image: img8, hex: hex8 };
      }
      case 9: {
        const mat = BUTTON_MATS.find(m => m.name === state.button);
        const hex9 = state.buttonColor ? h(state.buttonColor) : h(state.shellColor);
        const img9 = (mat && mat.colorImages && state.buttonColor ? mat.colorImages[state.buttonColor] : null)
          || (mat && mat.matchesShell ? getMatchesShellImage(BUTTON_MATS) : null)
          || (mat && mat.image) || null;
        return { image: img9, hex: hex9 };
      }
      case 10: {
        const allLogos = [...LOGO_METALLIC, ...LOGO_ZIRCOON, ...LOGO_FLAGS, LOGO_CRYSTAL];
        const lm = allLogos.find(c => c && c.name === state.logo);
        return { image: (lm && lm.image) || null, hex: (lm && lm.top) || null };
      }
      default:
        return { image: null, hex: null };
    }
  }

  const sp = n => resolveVisual(n);

  const items = [];
  if (state.coating && state.shellColor)
    items.push({ label:'Shell', value:`${state.coating} / ${state.shellColor}`, ...sp(1) });
  if (state.frontInsert)
    items.push({ label:'Front Insert', value:state.frontInsert + (state.frontInsertColor ? ' — '+state.frontInsertColor : ''), ...sp(2) });
  if (state.rearInsert)
    items.push({ label:'Rear Insert', value:state.rearInsert + (state.rearInsertColor ? ' — '+state.rearInsertColor : ''), ...sp(3) });
  if (state.visorMaterial)
    items.push({ label:'Visor', value:`${state.visorType} / ${state.visorMaterial}${state.visorColor ? ' / '+state.visorColor : ''}`, ...sp(4) });
  if (state.strap)
    items.push({ label:'Strap', value:state.strap + (state.strapColor ? ' / '+state.strapColor : ''), ...sp(5) });
  if (state.frame) {
    const fval = state.frame + (state.frameColor ? ' / '+state.frameColor : '');
    items.push({ label:'Frame', value:fval, ...sp(6) });
  }
  if (state.frameSwarovski && state.frameSwarovskiColor) {
    const swEntry = findSwarovskiColor(state.frameSwarovskiColor);
    items.push({ label:'Frame Swarovski', value:state.frameSwarovskiColor, image: swEntry ? swEntry[2] : null, hex: swEntry ? swEntry[1] : null, smallImage:true });
  }
  if (state.grid)
    items.push({ label:'Grid', value:state.grid + (state.gridColor ? ' / '+state.gridColor : ''), ...sp(7) });
  if (state.subgrid)
    items.push({ label:'Subgrid', value:state.subgrid + (state.subgridColor ? ' / '+state.subgridColor : ''), ...sp(8) });
  if (state.button)
    items.push({ label:'Button', value:state.button + (state.buttonColor ? ' / '+state.buttonColor : ''), ...sp(9) });
  if (state.logo)
    items.push({ label:'Logo', value:state.logo, ...sp(10) });
  if (state.size)
    items.push({ label:'Size', value:`${state.size} — ${state.lining}`, image:null, hex:null, sizeText:state.size, sizeSub:state.lining });
  if (state.nameTag && state.nameTag !== 'None') {
    const ntOpt = NAMETAG_OPTIONS.find(o => o.name === state.nameTag);
    items.push({ label:'Name Tag', value:state.nameTag + (state.nameTagText ? ` — ${state.nameTagText}` : ''), image: ntOpt ? ntOpt.image : null, hex:null });
  }

  grid.innerHTML = items.map(item => {
    const visual = item.image
      ? `<img src="${item.image}" alt="${item.label}" style="width:${item.smallImage ? '55%' : '100%'};height:${item.smallImage ? '55%' : '100%'};object-fit:contain;object-position:center;">`
      : item.hex
        ? `<div class="review-swatch" style="background:${item.hex};"></div>`
        : item.sizeText
          ? `<div class="review-size-display"><span class="review-size-number">${item.sizeText}</span><span class="review-size-sub">${item.sizeSub}</span></div>`
          : '';
    return `<div class="review-grid-item">
      <div class="review-grid-visual">${visual}</div>
      <div class="review-grid-label">${item.label}</div>
      <div class="review-grid-value">${item.value}</div>
    </div>`;
  }).join('');
}

// ─── STEP 13 — Review & Order ─────────────────────────────────
function buildStep13() {
  const el = document.getElementById('step-13');
  if (!el) return;

  el.innerHTML = `
    <div class="step-header">
      <div class="step-header-row">
        <span class="step-number">13</span>
        <h2>Review &amp; Order</h2>
      </div>
    </div>
    <div class="step-body">
      ${_restoredFromLink ? `<div class="restored-link-notice" style="background:#fff8e6;border:1px solid #e8d9a0;border-radius:8px;padding:12px 16px;margin-bottom:16px;font-size:13px;color:#6b5a20;">This configuration was restored from a saved link. Prices reflect the moment the link was created and may have changed since — the final price is always confirmed at checkout.</div>` : ''}
      <div id="review-grid" class="review-grid"></div>
      <div id="order-summary" style="margin-top:24px;"></div>
      <div id="cart-errors" class="validation-errors hidden"></div>
      <div class="step-nav">
        <button class="btn-prev" id="s13-back">&larr; Back</button>
        <button class="btn-prev" id="s13-edit">&#9998; Edit from start</button>
        <button class="btn-prev" id="s13-share">&#128279; Copy share link</button>
        <button id="add-to-cart-btn" class="btn-cart btn-cart-inline">Add to cart</button>
      </div>
    </div>`;

  el.querySelector('#s13-back').addEventListener('click', () => showStep(12));
  el.querySelector('#s13-edit').addEventListener('click', () => showStep(1));
  el.querySelector('#add-to-cart-btn').addEventListener('click', () => submitToCart());
  el.querySelector('#s13-share').addEventListener('click', async () => {
    try {
      const link = encodeConfigLink();
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      showToast('Link copied — share it to save this configuration');
    } catch (e) {
      showToast('Could not copy the link, please try again');
    }
  });
}

// ─── VALIDATION ───────────────────────────────────────────────
function validateAll() {
  return !!(state.coating && state.shellColor && state.frontInsert &&
    state.rearInsert && state.visorMaterial && state.strap &&
    state.frame && state.grid && state.subgrid && state.button &&
    state.logo && state.size);
}

function showValidationErrors() {
  const missing = [];
  if (!state.coating || !state.shellColor) missing.push('Step 1: Shell coating and color');
  if (!state.frontInsert)  missing.push('Step 2: Front insert');
  if (!state.rearInsert)   missing.push('Step 3: Rear insert');
  if (!state.visorMaterial)missing.push('Step 4: Visor material');
  if (!state.strap)        missing.push('Step 5: Strap');
  if (!state.frame)        missing.push('Step 6: Frame');
  if (!state.grid)         missing.push('Step 7: Grid');
  if (!state.subgrid)      missing.push('Step 8: Subgrid');
  if (!state.button)       missing.push('Step 9: Button');
  if (!state.logo)         missing.push('Step 10: Logo');
  if (!state.size)         missing.push('Step 11: Size');
  const errEl = document.getElementById('validation-errors');
  if (errEl) {
    errEl.innerHTML = `<strong>Please complete these steps before continuing:</strong>
      <ul>${missing.map(m => `<li>${m}</li>`).join('')}</ul>`;
    errEl.classList.remove('hidden');
    errEl.scrollIntoView({ behavior:'smooth' });
  }
}

// ─── CART SUBMISSION ──────────────────────────────────────────
async function submitToCart() {
  const variantId = VARIANTS[state.coating].id;
  const total = getTotal();
  const fmtComp = (mat, col) => mat + (col ? ` / ${col}` : '');
  const properties = {
    'Shell Coating':        state.coating,
    'Shell Color':          state.shellColor,
    'Front Insert':         state.frontInsert + (state.frontInsertColor ? ` — ${state.frontInsertColor}` : ''),
    'Rear Insert':          state.rearInsert  + (state.rearInsertColor  ? ` — ${state.rearInsertColor}`  : ''),
    'Visor Type':           state.visorType,
    'Visor Material':       state.visorMaterial,
    'Visor Color':          state.visorColor || 'Standard',
    'Strap':                state.strap + (state.strapColor ? ` — ${state.strapColor}` : ''),
    'Frame':                state.frame + (state.frameColor ? ` / ${state.frameColor}` : '') + (state.frameSwarovski ? ` + Swarovski${state.frameSwarovskiColor ? ` (${state.frameSwarovskiColor})` : ''}` : ''),
    'Grid':                 fmtComp(state.grid,  state.gridColor),
    'Subgrid':              fmtComp(state.subgrid, state.subgridColor),
    'Button':               fmtComp(state.button,  state.buttonColor),
    'Logo':                 state.logo,
    'Size':                 state.size,
    'Lining':               state.lining,
    'Name Tag':             state.nameTag,
    'Name Tag Text':        state.nameTagText || '',
    'Configuration Total':  fmt(total)
  };

  const cartBtn = document.getElementById('add-to-cart-btn');
  if (cartBtn) { cartBtn.disabled = true; cartBtn.textContent = 'Adding to cart…'; }

  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, properties })
    });
    if (!res.ok) throw new Error('Cart error ' + res.status);
    window.location.href = '/cart';
  } catch (err) {
    if (cartBtn) { cartBtn.disabled = false; cartBtn.textContent = `Add to cart — ${fmt(total)}`; }
    const errEl = document.getElementById('cart-errors');
    if (errEl) {
      errEl.innerHTML = '<strong>Something went wrong adding to cart. Please try again.</strong>';
      errEl.classList.remove('hidden');
    }
  }
}

// ─── SAVE / SHARE CONFIGURATION ─────────────────────────────────
// Encodes a snapshot of the current build (choices + the prices already computed for them)
// into a URL so it can be saved or shared. No server/database — the link itself is the data.
// Prices are stored as-is (not recalculated on restore) to keep this simple and low-risk;
// a disclaimer is shown on restore in case prices changed since the link was made.
const SHARE_STATE_KEYS = [
  'coating','shellColor','shellColorHex',
  'frontInsert','frontInsertColor','frontInsertPrice',
  'rearInsert','rearInsertColor','rearInsertPrice',
  'visorType','visorMaterial','visorColor','visorPrice',
  'strap','strapColor','strapPrice',
  'frame','frameColor','framePrice','frameSwarovski','frameSwarovskiColor',
  'grid','gridColor','gridPrice',
  'subgrid','subgridColor','subgridPrice',
  'button','buttonColor','buttonPrice',
  'logo','logoPrice',
  'size','lining',
  'nameTag','nameTagText','nameTagPrice',
  'basePrice'
];

let _restoredFromLink = false;

// Encoded as a positional array (values only, in SHARE_STATE_KEYS order) rather than
// {key:value} JSON — cuts the link length by roughly 40-50% by dropping the repeated
// field names. IMPORTANT: only ever append new entries to the end of SHARE_STATE_KEYS,
// never insert/reorder — this list's order IS the wire format.
function encodeConfigLink() {
  const values = SHARE_STATE_KEYS.map(k => state[k]);
  const json = JSON.stringify(values);
  const b64 = btoa(encodeURIComponent(json).replace(/%([0-9A-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16))));
  const url = new URL(window.location.href);
  url.search = '';
  url.searchParams.set('config', b64);
  return url.toString();
}

function decodeConfigLink(b64) {
  try {
    const percentEncoded = Array.prototype.map.call(atob(b64), c => '%' + c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
    const values = JSON.parse(decodeURIComponent(percentEncoded));
    if (!Array.isArray(values)) return null;
    const snapshot = {};
    SHARE_STATE_KEYS.forEach((k, i) => { snapshot[k] = values[i]; });
    return snapshot;
  } catch (e) {
    return null;
  }
}

// Restores state from a ?config= link if present. Returns true only if a usable
// configuration was found — any missing/broken/unexpected data just returns false
// and the configurator starts fresh at step 1, same as any normal visit.
function restoreConfigFromUrl() {
  try {
    const b64 = new URLSearchParams(window.location.search).get('config');
    if (!b64) return false;
    const snapshot = decodeConfigLink(b64);
    if (!snapshot || !snapshot.coating || !snapshot.shellColor) return false;
    SHARE_STATE_KEYS.forEach(k => {
      if (snapshot[k] !== undefined) state[k] = snapshot[k];
    });
    _restoredFromLink = true;
    return true;
  } catch (e) {
    return false;
  }
}

// ─── DOMAIN LICENSE CHECK ───────────────────────────────────────
// Built by TCO Works for abouthelmets.com. If this file ends up running on an
// unrecognized domain, show a licensing notice instead of the configurator.
// Fails open on purpose: any unexpected hostname format or error here still
// lets the configurator run normally — this must never break the real site.
const ALLOWED_HOSTS = ['abouthelmets.com', 'www.abouthelmets.com', '259491.myshopify.com'];
function isAllowedHost() {
  try {
    const host = window.location.hostname.toLowerCase();
    if (ALLOWED_HOSTS.includes(host)) return true;
    if (host.endsWith('.shopifypreview.com')) return true;
    if (host === 'localhost' || host === '127.0.0.1') return true;
    return false;
  } catch (e) {
    return true;
  }
}

// ─── INIT ─────────────────────────────────────────────────────
function init() {
  if (!isAllowedHost()) {
    const layout = document.querySelector('.cromo-layout');
    if (layout) {
      layout.innerHTML = `<div style="padding:80px 20px;text-align:center;font-family:sans-serif;max-width:480px;margin:0 auto;">
        <p style="font-size:16px;margin-bottom:12px;">This configurator is licensed for a specific store.</p>
        <p style="font-size:14px;color:#666;">Interested in using it for your own store? Contact <a href="https://tcoworks.nl" target="_blank" rel="noopener">tcoworks.nl</a>.</p>
      </div>`;
    }
    return;
  }
  buildStep1();
  buildInsertStep(2, false);
  buildInsertStep(3, true);
  buildStep4();
  buildStep5();
  buildStep6();
  COMPONENT_CFGS.forEach(buildComponentStep);
  buildStep10();
  buildStep11();
  buildStep12();
  const restored = restoreConfigFromUrl();
  buildStep13();
  showStep(restored ? 13 : 1);
  updatePriceDisplay();
}

document.addEventListener('DOMContentLoaded', init);